import { ChevronLeft, ChevronRight, MoreVertical, UserPlus } from "lucide-react";
import { useEffect, useState, useCallback } from "react";
import {
  Card,
  CardContent,
  CardDescription,    
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Checkbox } from "../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Label } from "../components/ui/label";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import {
  Search,
  Plus,
  Mail,
  Phone,
  MapPin,
  Calendar,
  Building2,
  User,
  Edit,
  Trash2,
  Eye,
  EyeOff,
  FileText,
  Briefcase,
  DollarSign,
  Download,
} from "lucide-react";
import {
  getEmployees,
  createEmployee,
  updateEmployee,
  deleteEmployee,
  getEmployeeById,
  uploadEmployeeExcel,
  downloadEmployeeTemplate,
  permanentDeleteEmployee,
  reactivateEmployee,
  downloadJoiningLetter,
  downloadOfferLetter,
  sendOfferLetterEmail,
  sendjoiningLetterEmail,
  downloadSalarySlip,
  sendSalarySlipEmail,
} from "../api/employees";
import { getCompanyComplianceSettings } from "../api/officeandcompany";
import { getManagerList, getDepartmentList, getDesignationList } from "../redux/slices/employee";
import { User as UserType } from "../types";
import { useToast } from "../hooks/useToast";
import { useAuth } from "../contexts/AuthContext";
import { country } from "../_mock/country";
import { state } from "../_mock/state";
import { dispatch, useSelector } from "../redux/store";
import api from "../api/api";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator
} from "../components/ui/dropdown-menu";
import termsConditionsApi from '../api/TermsconditionApi';

// ==================== STATUTORY DATA TABLES ====================

// Professional Tax — state-wise monthly slabs (FY 2025-26)
const PT_STATES: Record<string, { label: string; slabs: number[][]; annual: boolean; note?: string }> = {
  karnataka: { label: "Karnataka", slabs: [[0, 14999, 0], [15000, 99999999, 200]], annual: false },
  maharashtra: { label: "Maharashtra", slabs: [[0, 7500, 0], [7501, 10000, 175], [10001, 99999999, 200]], annual: false },
  westbengal: { label: "West Bengal", slabs: [[0, 8500, 0], [8501, 10000, 90], [10001, 15000, 110], [15001, 25000, 130], [25001, 40000, 150], [40001, 99999999, 200]], annual: false },
  telangana: { label: "Telangana", slabs: [[0, 14999, 0], [15000, 20000, 150], [20001, 99999999, 200]], annual: false },
  tamilnadu: { label: "Tamil Nadu", slabs: [[0, 3500, 0], [3501, 5000, 22], [5001, 7500, 33], [7501, 10000, 52], [10001, 12500, 63], [12501, 15000, 83], [15001, 20000, 104], [20001, 25000, 135], [25001, 99999999, 182]], annual: false },
  gujarat: { label: "Gujarat", slabs: [[0, 5999, 0], [6000, 8999, 80], [9000, 11999, 150], [12000, 99999999, 200]], annual: false },
  kerala: { label: "Kerala", slabs: [[0, 99999999, 208]], annual: false },
  andhra: { label: "Andhra Pradesh", slabs: [[0, 14999, 0], [15000, 20000, 150], [20001, 99999999, 200]], annual: false },
  mp: { label: "Madhya Pradesh", slabs: [[0, 18749, 0], [18750, 25000, 125], [25001, 33333, 167], [33334, 99999999, 208]], annual: false },
  assam: { label: "Assam", slabs: [[0, 9999, 0], [10000, 14999, 150], [15000, 24999, 180], [25000, 99999999, 208]], annual: false },
  bihar: { label: "Bihar", slabs: [[0, 99999999, 0]], annual: false, note: "No PT in Bihar" },
  delhi: { label: "Delhi", slabs: [[0, 99999999, 0]], annual: false, note: "No PT in Delhi" },
  rajasthan: { label: "Rajasthan", slabs: [[0, 99999999, 0]], annual: false, note: "No PT in Rajasthan" },
  none: { label: "Not Applicable", slabs: [[0, 99999999, 0]], annual: false },
};

// Labour Welfare Fund — state-wise
const LWF_STATES: Record<string, { label: string; ee: number; er: number; freq: string }> = {
  andhra: { label: "Andhra Pradesh", ee: 20, er: 40, freq: "annual" },
  delhi: { label: "Delhi", ee: 0.75, er: 2.25, freq: "monthly" },
  gujarat: { label: "Gujarat", ee: 6, er: 12, freq: "halfyearly" },
  haryana: { label: "Haryana", ee: 25, er: 50, freq: "monthly" },
  karnataka: { label: "Karnataka", ee: 20, er: 40, freq: "annual" },
  kerala: { label: "Kerala", ee: 4, er: 8, freq: "halfyearly" },
  madhyapradesh: { label: "Madhya Pradesh", ee: 10, er: 20, freq: "halfyearly" },
  maharashtra: { label: "Maharashtra", ee: 25, er: 75, freq: "halfyearly" },
  odisha: { label: "Odisha", ee: 10, er: 20, freq: "annual" },
  punjab: { label: "Punjab", ee: 10, er: 20, freq: "monthly" },
  rajasthan: { label: "Rajasthan", ee: 25, er: 50, freq: "monthly" },
  tamilnadu: { label: "Tamil Nadu", ee: 10, er: 20, freq: "halfyearly" },
  telangana: { label: "Telangana", ee: 20, er: 40, freq: "annual" },
  westbengal: { label: "West Bengal", ee: 3, er: 15, freq: "monthly" },
  none: { label: "Not Applicable", ee: 0, er: 0, freq: "monthly" },
};

// TAX COMPUTATION FY 2025-26 (AY 2026-27)

// New Tax Regime slabs (Budget 2025)
function taxNew(taxable: number): number {
  const slabs: number[][] = [
    [0, 400000, 0],
    [400001, 800000, 0.05],
    [800001, 1200000, 0.10],
    [1200001, 1600000, 0.15],
    [1600001, 2000000, 0.20],
    [2000001, 2400000, 0.25],
    [2400001, Infinity, 0.30],
  ];
  let tax = 0;
  for (const [lo, hi, rate] of slabs) {
    if (taxable <= lo) break;
    tax += (Math.min(taxable, hi) - lo) * rate;
  }
  // Rebate u/s 87A: up to ₹60,000 if taxable income ≤ ₹12,00,000
  if (taxable <= 1200000) tax = Math.max(0, tax - Math.min(tax, 60000));
  return tax + tax * 0.04; // + 4% cess
}

// Old Tax Regime slabs (below 60 years)
function taxOld(taxable: number): number {
  const slabs: number[][] = [
    [0, 250000, 0],
    [250001, 500000, 0.05],
    [500001, 1000000, 0.20],
    [1000001, Infinity, 0.30],
  ];
  let tax = 0;
  for (const [lo, hi, rate] of slabs) {
    if (taxable <= lo) break;
    tax += (Math.min(taxable, hi) - lo) * rate;
  }
  // Rebate u/s 87A: up to ₹12,500 if taxable ≤ ₹5,00,000
  if (taxable <= 500000) tax = Math.max(0, tax - Math.min(tax, 12500));
  return tax + tax * 0.04; // + 4% cess
}

function calcTDS(
  grossAnnual: number,
  regime: string,
  declaration: {
    sec80c?: number;
    sec80d?: number;
    sec80ccd1b?: number;
    hraExempt?: number;
    homeLoan?: number;
    ltaExempt?: number;
    sec80g?: number;
    npsEmployer?: number;
    ptPaid?: number;
  },
  pfEEAnnual: number
): number {
  if (regime === "new") {
    const stdDeduction = 75000;
    const npsEmployerAnnual = (declaration.npsEmployer || 0) * 12;
    const taxableIncome = Math.max(0, grossAnnual - stdDeduction - npsEmployerAnnual);
    return Math.round(taxNew(taxableIncome) / 12);
  } else {
    // Old regime deductions
    const stdDeduction = 50000;
    const hraExemptAnnual = declaration.hraExempt || 0;
    const ltaExemptAnnual = declaration.ltaExempt || 0;
    const sec80cAnnual = Math.min((declaration.sec80c || 0) + pfEEAnnual, 150000);
    const sec80dAnnual = Math.min(declaration.sec80d || 0, 50000);
    const sec80ccd1bAnnual = Math.min(declaration.sec80ccd1b || 0, 50000);
    const homeLoanAnnual = Math.min(declaration.homeLoan || 0, 200000);
    const sec80gAnnual = declaration.sec80g || 0;
    const ptAnnual = (declaration.ptPaid || 0) * 12;

    const taxableIncome = Math.max(
      0,
      grossAnnual - stdDeduction - hraExemptAnnual - ltaExemptAnnual - sec80cAnnual -
      sec80dAnnual - sec80ccd1bAnnual - homeLoanAnnual - sec80gAnnual - ptAnnual
    );
    return Math.round(taxOld(taxableIncome) / 12);
  }
}

const ROLE_OPTIONS_BY_LOGIN_ROLE: Record<string, string[]> = {
  admin: ["director", "founder", "hr head", "manager", "supervisor", "employee"],
  founder: ["director", "hr head", "manager", "supervisor", "employee"],
  director: ["hr head", "manager", "supervisor", "employee"],
  "hr head": ["manager", "supervisor", "employee"],
  manager: ["supervisor", "employee"],
  supervisor: ["employee"],
};

const DESIGNATION_OPTIONS = [
  "Backend Developer",
  "Frontend Developer",
  "Full Stack Developer",
  "DevOps Engineer",
  "Data Scientist",
  "QA Engineer",
  "UI/UX Designer",
  "Product Manager",
  "HR Manager",
  "Sales Executive",
  "Marketing Manager",
  "Accountant"
];

// Helper functions
function calcPT(grossMonthly: number, stateKey: string): number {
  const cfg = PT_STATES[stateKey] || PT_STATES.none;
  for (const [lo, hi, amt] of cfg.slabs) {
    if (grossMonthly >= lo && grossMonthly <= hi) return amt;
  }
  return 0;
}

function lwfMonthly(stateKey: string): { ee: number; er: number } {
  const s = LWF_STATES[stateKey] || LWF_STATES.none;
  if (s.freq === "monthly") return { ee: s.ee, er: s.er };
  if (s.freq === "halfyearly") return { ee: +(s.ee / 6).toFixed(2), er: +(s.er / 6).toFixed(2) };
  if (s.freq === "annual") return { ee: +(s.ee / 12).toFixed(2), er: +(s.er / 12).toFixed(2) };
  return { ee: 0, er: 0 };
}

function fmt(n: number | string): string {
  const num = typeof n === 'string' ? parseFloat(n) : n;
  if (isNaN(num)) return "0.00";
  return new Intl.NumberFormat("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(num || 0);
}

function num(v: any): number {
  return parseFloat(v) || 0;
}

// ==================== MAIN EMPLOYEES COMPONENT ====================

export function Employees() {
  const [employees, setEmployees] = useState<UserType[]>([]);
  const [filteredEmployees, setFilteredEmployees] = useState<UserType[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("all");
  const [roleFilter, setRoleFilter] = useState("all");
  const [selectedEmployee, setSelectedEmployee] = useState<UserType | null>(null);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedEmployeeForDelete, setSelectedEmployeeForDelete] = useState<UserType | null>(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [file, setFile] = useState<File | null>(null);

  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [activeTab, setActiveTab] = useState<"personal" | "employment" | "documents">("personal");
  const [selectedEmployeeForDocs, setSelectedEmployeeForDocs] = useState<UserType | null>(null);
  const { toast } = useToast();
  const { user } = useAuth();
  const [showMandatoryModal, setShowMandatoryModal] = useState(false);

  const [settings, setSettings] = useState({
    disable_mandatory: false,
    is_active: true,
  });

  const canManageEmployees = user?.role === "admin" || user?.role === "hr head" || user?.role === "manager" || user?.role === "supervisor" || user?.role === "director" || user?.role === "founder";

  useEffect(() => {
    fetchEmployees();
  }, []);

  useEffect(() => {
    dispatch(getDesignationList());
  }, []);

  useEffect(() => {
    filterEmployees();
  }, [employees, searchQuery, departmentFilter, roleFilter]);

  useEffect(() => {
    fetchMandatorySettings();
  }, []);

  const fetchMandatorySettings = async () => {
    try {
      const res = await api.get("/get_employee_setting");
      // setSettings(res.data);
      setSettings({
        disable_mandatory: res.data?.employee_setting?.disable_mandatory ?? false,
        is_active: res.data?.employee_setting?.is_active ?? true,
      });
    } catch (err) {
      console.error("Error fetching settings", err);
    }
  };

  const fetchEmployees = async () => {
    try {
      console.log("Fetching employees...");
      const response = await getEmployees();
      console.log("Employees fetched successfully:", response.employees?.length || 0);
      setEmployees(response.employees || []);
    } catch (error: any) {
      console.error("Error fetching employees:", error);
      toast({
        // title: "Error",
        description: error.message || error.detail || "Failed to load employees",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const designationList = useSelector((state: any) => state.employee.designationList);

  const getDesignationName = (designationId: string) => {
    const designation = designationList.find((d: any) => d.id === designationId);
    return designation?.name || designationId;
  };

  const filterEmployees = () => {
    let filtered = employees;

    if (searchQuery) {
      filtered = filtered.filter(
        (emp) =>
          emp.first_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          emp.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          emp.employeeID?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    if (departmentFilter !== "all") {
      filtered = filtered.filter((emp) => emp.department === departmentFilter);
    }

    if (roleFilter !== "all") {
      filtered = filtered.filter((emp) => emp.role === roleFilter);
    }

    setFilteredEmployees(filtered);
  };

  const handleAddEmployee = async (employeeData: Partial<UserType>) => {
    try {
      console.log("Adding new employee:", employeeData);
      await createEmployee(employeeData);
      toast({
        title: "Success",
        description: "Employee added successfully",
      });
      setShowAddModal(false);
      fetchEmployees();
    } catch (error: any) {
      console.error("Error adding employee:", error);
      toast({
        // title: "Error",
        description: error.message || error.detail || "Employee already exists",
      });
    }
  };

  const handleEditEmployee = async (employeeData: Partial<UserType>) => {
    console.log("Updating employee:", employeeData.id);
    console.log("Payload:", employeeData);

    if (!employeeData.id) {
      toast({
        // title: "Error",
        description: "Employee ID missing",
        variant: "destructive",
      });
      return;
    }

    await updateEmployee(employeeData.id, employeeData);
    const fresh = await getEmployeeById(employeeData.id);
    setSelectedEmployee(fresh.employee);

    toast({
      title: "Success",
      description: "Employee updated successfully",
    });

    setShowEditModal(false);
    fetchEmployees();
  };

  const handleDeleteClick = (employee: UserType) => {
    setSelectedEmployeeForDelete(employee);
    setDeleteModalOpen(true);
  };

  const handleSoftDelete = async () => {
    if (!selectedEmployeeForDelete) return;
    await deleteEmployee(selectedEmployeeForDelete.id);
    toast({ title: "Employee deactivated" });
    setDeleteModalOpen(false);
    fetchEmployees();
  };

  const handlePermanentDelete = async () => {
    if (!selectedEmployeeForDelete) return;
    await permanentDeleteEmployee(selectedEmployeeForDelete.id);
    toast({ title: "Employee permanently deleted" });
    setDeleteModalOpen(false);
    fetchEmployees();
  };

  const handleReactivate = async () => {
    if (!selectedEmployeeForDelete) return;
    await reactivateEmployee(selectedEmployeeForDelete.id);
    toast({ title: "Employee reactivated" });
    setDeleteModalOpen(false);
    fetchEmployees();
  };

  // const handleViewEmployee = async (id: string) => {
  //   try {
  //     console.log("Fetching employee details:", id);
  //     const response = await getEmployeeById(id);
  //     setSelectedEmployee(response.employee);
  //     setShowViewModal(true);
  //   } catch (error: any) {
  //     console.error("Error fetching employee details:", error);
  //     toast({
  //       title: "Error",
  //       description: error.message || "Failed to load employee details",
  //       variant: "destructive",
  //     });
  //   }
  // };

  const handleViewEmployee = async (id: string, openTab?: "personal" | "employment" | "documents") => {
    try {
      console.log("Fetching employee details:", id);
      const response = await getEmployeeById(id);
      setSelectedEmployee(response.employee);
      setActiveTab(openTab || "personal");
      setShowViewModal(true);
    } catch (error: any) {
      console.error("Error fetching employee details:", error);
      toast({
        // title: "Error",
        description: error.message || error.detail || "Failed to load employee details",
        variant: "destructive",
      });
    }
  };

  // Add function to open documents tab directly
  const handleOpenDocumentsTab = (employee: UserType) => {
    handleViewEmployee(employee.id, "documents");
  };


  const handleEditClick = async (id: string) => {
    try {
      const data = await getEmployeeById(id);
      setSelectedEmployee(data.employee);
      setShowEditModal(true);
    } catch (error: any) {
      toast({
        // title: "Error",
        description: error.message || error.detail || "Failed to load employee",
        variant: "destructive",
      });
    }
  };

  const departments = [...new Set(employees.map((emp) => emp.department))];
  const roles = [...new Set(employees.map((emp) => emp.role))];
  const [importResults, setImportResults] = useState<{
    success: Array<{ name: string; email: string; employeeId?: string }>;
    failed: Array<{ name: string; email: string; reason: string }>;
    show: boolean;
  }>({ success: [], failed: [], show: false });


  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="gt-shell min-h-screen">
      <div className="max-w-7xl mx-auto p-4 md:p-7 space-y-6 gt-fade-in">
      <div className="gt-page-hero flex-col md:flex-row gap-4">
        <div>
          <div className="gt-eyebrow-row">
            <span className="gt-leaf" />
            <span className="gt-eyebrow">ii. People</span>
          </div>
          <h1 className="gt-page-title">Employee <em>Management</em></h1>
          <p className="gt-page-sub" style={{ marginTop: 6 }}>
            Manage your organization's <em className="gt-serif-italic">workforce</em>.
          </p>
        </div>
        <div className="flex gap-3 flex-wrap">
          {canManageEmployees && (

            <>
              <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
                <DialogTrigger asChild>
                  <Button className="gt-btn is-primary is-lg">
                    <Plus className="h-4 w-4" />
                    Add Employee
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-6xl bg-white max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Add New Employee</DialogTitle>
                    <DialogDescription>
                      Fill in the details to add a new employee to your organization.
                    </DialogDescription>
                  </DialogHeader>
                  <EmployeeForm
                    onSubmit={handleAddEmployee}
                    currentUserRole={user?.role || "admin"}
                  />
                </DialogContent>
              </Dialog>

              <Button
                onClick={() => setShowImportModal(true)}
                className="gt-btn is-ghost is-lg"
              >
                <Plus className="h-4 w-4" />
                Import Employee
              </Button>


              <Dialog open={showImportModal} onOpenChange={setShowImportModal}>
                <DialogContent className="max-w-md bg-white">
                  <DialogHeader>
                    <DialogTitle>Import Employees</DialogTitle>
                    <DialogDescription>
                      Upload Excel file to import employees in bulk
                    </DialogDescription>
                  </DialogHeader>

                  <div className="space-y-4">
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={async () => {
                        try {
                          await downloadEmployeeTemplate();
                        } catch (err: any) {
                          console.error("Template download error:", err);
                          toast({
                            description:
                              err?.response?.data?.detail ||
                              err?.message ||
                              "Could not download template.",
                            variant: "destructive",
                          });
                        }
                      }}
                    >
                      Download Sample Excel
                    </Button>

                    <div>
                      <Label>Select Excel File</Label>
                      <Input
                        type="file"
                        accept=".xlsx,.xls"
                        onChange={(e) => setFile(e.target.files?.[0] || null)}
                      />
                    </div>

                    <Button
                      className="w-full bg-blue-600 text-white"
                      onClick={async () => {
                        if (!file) {
                          toast({
                            // title: "Error",
                            description: "Please select a file",
                            variant: "destructive",
                          });
                          return;
                        }
                        try {
                          const response = await uploadEmployeeExcel(file);
                          console.log("Import response:", response);

                          // Extract results from response
                          // const successList = response.successful_imports || response.success || [];
                          // const failedList = response.failed_imports || response.failed || [];

                          const successList = response.created || [];
                          const failedList = response.failed || [];

                          setImportResults({
                            success: successList.map((item: any) => ({
                              name: item.name || item.full_name || item.employeeID || "-",
                              email: item.email,
                              employeeId: item.employeeID || item.employee_id
                            })),
                            failed: failedList.map((item: any) => ({
                              name: item.name || item.employeeID || "Unknown",
                              email: item.email || "N/A",
                              reason: item.reason || item.error || "Invalid data format"
                            })),
                            show: true
                          });

                          toast({
                            title: "Import Completed",
                            description: `${successList.length} employees imported successfully, ${failedList.length} failed`,
                            variant: successList.length > 0 ? "default" : "destructive",
                          });

                          setShowImportModal(false);
                          setFile(null);
                          fetchEmployees();
                        } catch (err: any) {
                          console.error("Import error:", err);

                          // Check if error contains detailed results
                          if (err.response?.data) {
                            const errorData = err.response.data;
                            const successList = errorData.successful_imports || errorData.success || [];
                            const failedList = errorData.failed_imports || errorData.failed || [];

                            if (successList.length > 0 || failedList.length > 0) {
                              setImportResults({
                                success: successList.map((item: any) => ({
                                  name: item.name || item.full_name || `${item.first_name} ${item.last_name}`,
                                  email: item.email,
                                  employeeId: item.employeeID || item.employee_id
                                })),
                                failed: failedList.map((item: any) => ({
                                  name: item.name || item.row_data?.name || "Unknown",
                                  email: item.email || item.row_data?.email || "N/A",
                                  reason: item.reason || item.error || "Invalid data format"
                                })),
                                show: true
                              });
                              setShowImportModal(false);
                              setFile(null);
                              if (successList.length > 0) {
                                fetchEmployees();
                              }
                              return;
                            }
                          }

                          toast({
                            title: "Import Failed",
                            description: err.message || err.response?.data?.message || "Upload failed. Please check file format.",
                            variant: "destructive",
                          });
                        }
                      }}
                    >
                      Upload Excel
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>

              <Dialog open={importResults.show} onOpenChange={(open) => !open && setImportResults(prev => ({ ...prev, show: false }))}>
                <DialogContent className="max-w-2xl bg-white max-h-[80vh] overflow-hidden flex flex-col">
                  <DialogHeader>
                    <DialogTitle className="text-xl">Import Results</DialogTitle>
                    <DialogDescription>
                      Summary of employee import operation
                    </DialogDescription>
                  </DialogHeader>

                  <Tabs defaultValue="success" className="flex-1 overflow-hidden flex flex-col">
                    <TabsList className="grid w-full grid-cols-2">
                      <TabsTrigger value="success" className="flex items-center gap-2">
                        <div className="h-2 w-2 rounded-full bg-green-500"></div>
                        Successful ({importResults.success.length})
                      </TabsTrigger>
                      <TabsTrigger value="failed" className="flex items-center gap-2">
                        <div className="h-2 w-2 rounded-full bg-red-500"></div>
                        Failed ({importResults.failed.length})
                      </TabsTrigger>
                    </TabsList>

                    <TabsContent value="success" className="flex-1 overflow-auto mt-4">
                      {importResults.success.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                          <div className="text-4xl mb-2">📭</div>
                          <p>No employees were imported successfully</p>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <div className="bg-green-50 p-3 rounded-lg mb-3">
                            <p className="text-green-800 text-sm font-medium">
                              ✅ {importResults.success.length} employee(s) added successfully
                            </p>
                          </div>
                          <div className="border rounded-lg overflow-hidden">
                            <table className="min-w-full divide-y divide-gray-200">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">EmpID</th>
                                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                                  {/* <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Employee ID</th> */}
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-200">
                                {importResults.success.map((employee, idx) => (
                                  <tr key={idx} className="hover:bg-gray-50">
                                    <td className="px-4 py-2 text-sm text-gray-900">{employee.name}</td>
                                    <td className="px-4 py-2 text-sm text-gray-600">{employee.email}</td>
                                    {/* <td className="px-4 py-2 text-sm font-mono text-gray-600">{employee.employeeId || "-"}</td> */}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </TabsContent>

                    <TabsContent value="failed" className="flex-1 overflow-auto mt-4">
                      {importResults.failed.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                          <div className="text-4xl mb-2">🎉</div>
                          <p>All employees were imported successfully!</p>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <div className="bg-red-50 p-3 rounded-lg mb-3">
                            <p className="text-red-800 text-sm font-medium">
                              ❌ {importResults.failed.length} employee(s) failed to import
                            </p>
                            <p className="text-red-600 text-xs mt-1">Please check the errors and fix them in your Excel file</p>
                          </div>
                          <div className="border rounded-lg overflow-hidden">
                            <table className="min-w-full divide-y divide-gray-200">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">EmpID</th>
                                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Reason</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-200">
                                {importResults.failed.map((employee, idx) => (
                                  <tr key={idx} className="hover:bg-red-50">
                                    <td className="px-4 py-2 text-sm text-gray-900">{employee.name}</td>
                                    <td className="px-4 py-2 text-sm text-gray-600">{employee.email}</td>
                                    <td className="px-4 py-2 text-sm text-red-600">{employee.reason}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </TabsContent>
                  </Tabs>

                  <DialogFooter className="mt-4 pt-4 border-t">
                    <Button onClick={() => setImportResults(prev => ({ ...prev, show: false }))}>
                      Close
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>


              <Dialog open={showMandatoryModal} onOpenChange={setShowMandatoryModal}>
                <DialogContent className="max-w-md bg-white">
                  <DialogHeader>
                    <DialogTitle>Mandatory Settings</DialogTitle>
                    <DialogDescription>
                      Control employee form validation
                    </DialogDescription>
                  </DialogHeader>

                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={settings.disable_mandatory}
                        onChange={(e) =>
                          setSettings({
                            ...settings,
                            disable_mandatory: e.target.checked,
                          })
                        }
                      />

                      <label className="text-sm">
                        Disable Mandatory Fields
                      </label>
                    </div>

                    <Button
                      className="w-full bg-blue-600 text-white"
                      onClick={async () => {
                        await api.put("/update_employee_setting", settings);
                        setShowMandatoryModal(false);
                      }}
                    >
                      Save
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>

              <Button
                onClick={() => setShowMandatoryModal(true)}
                className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700"
              >
                ⚙ Employee Settings
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Filters */}
      <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-lg">
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  placeholder="Search employees..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Filter by department" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="all">All Departments</SelectItem>
                {departments.map((dept) => (
                  <SelectItem key={dept} value={dept}>
                    {dept}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Filter by role" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="all">All Roles</SelectItem>
                {roles.map((role) => (
                  <SelectItem key={role} value={role}>
                    {role}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Employee Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {filteredEmployees.map((employee) => (
          <Card
            key={employee.id}
            className="group bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-lg transition-all duration-300 hover:scale-[1.02] relative overflow-hidden"
          >
            {/* Decorative gradient border */}
            <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300 -z-10 rounded-2xl" />
            <div className="absolute inset-[1px] bg-white rounded-2xl" />

            <div className="relative z-10 p-5">

              {/* FIXED HEADER (flex based) */}
              <div className="flex items-start justify-between mb-4">

                {/* LEFT SIDE */}
                <div className="flex items-start gap-3 min-w-0">
                  <Avatar className="h-14 w-14 ring-2 ring-gray-100">
                    <AvatarImage src={employee.profilePhoto} alt={employee.name} />
                    <AvatarFallback className="bg-gradient-to-br from-blue-500 to-purple-600 text-white font-semibold">
                      {employee.name?.charAt(0) || employee.first_name?.charAt(0) || "U"}
                    </AvatarFallback>
                  </Avatar>

                  <div className="min-w-0">
                    <h3 className="font-semibold text-gray-900 text-base truncate">
                      {employee.name || `${employee.first_name} ${employee.last_name}`}
                    </h3>

                    <p className="text-sm text-gray-500 mt-0.5 truncate">
                      {employee.designation} • {employee.department}
                    </p>

                    <div className="mt-1 flex items-center gap-1.5 flex-wrap">
                      <Badge
                        variant={employee.is_active ? "default" : "secondary"}
                        className={`${employee.is_active
                          ? "bg-green-50 text-green-700 border-green-200"
                          : "bg-gray-50 text-gray-600 border-gray-200"
                          } border text-xs px-2 py-0 font-normal`}
                      >
                        {employee.is_active ? "● Active" : "○ Inactive"}
                      </Badge>
                    </div>
                  </div>
                </div>

                {/* RIGHT SIDE (3 DOT MENU - FIXED ALIGNMENT) */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0 rounded-full hover:bg-gray-100"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <MoreVertical className="h-4 w-4 text-gray-600" />
                    </Button>
                  </DropdownMenuTrigger>

                  <DropdownMenuContent align="end" className="w-48 bg-white">
                    <DropdownMenuItem
                      onClick={() => handleViewEmployee(employee.id, "personal")}
                      className="cursor-pointer"
                    >
                      <Eye className="h-4 w-4 mr-2" />
                      View Details
                    </DropdownMenuItem>

                    {canManageEmployees && (
                      <>
                        <DropdownMenuItem
                          onClick={() => handleEditClick(employee.id)}
                          className="cursor-pointer"
                        >
                          <Edit className="h-4 w-4 mr-2" />
                          Edit Employee
                        </DropdownMenuItem>

                        <DropdownMenuItem
                          onClick={() => handleDeleteClick(employee)}
                          className="cursor-pointer text-red-600 focus:text-red-600"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete/Deactivate
                        </DropdownMenuItem>

                        <DropdownMenuSeparator />

                        <DropdownMenuItem
                          onClick={() => handleOpenDocumentsTab(employee)}
                          className="cursor-pointer"
                        >
                          <FileText className="h-4 w-4 mr-2" />
                          Send Documents
                        </DropdownMenuItem>
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              {/* Details Section */}
              <div className="space-y-2 mt-4">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Mail className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                  <span className="truncate">{employee.email}</span>
                </div>

                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <User className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                  <span className="font-mono text-xs">{employee.employeeID}</span>
                </div>

                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Calendar className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                  <span>
                    Joined{" "}
                    {new Date(employee.hire_date).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric'
                    })}
                  </span>
                </div>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* View Employee Modal */}
      <Dialog open={showViewModal} onOpenChange={setShowViewModal}>
        <DialogContent className="max-w-4xl bg-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Employee Details</DialogTitle>
          </DialogHeader>
          {selectedEmployee?.employee && (
            <EmployeeDetails
              employee={selectedEmployee.employee}
              activeTab={activeTab}
              onTabChange={setActiveTab}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Employee Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-6xl bg-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Employee</DialogTitle>
            <DialogDescription>Update employee information.</DialogDescription>
          </DialogHeader>
          {selectedEmployee?.employee && (
            <EmployeeForm
              employee={selectedEmployee.employee}
              onSubmit={handleEditEmployee}
              currentUserRole={user?.role || "admin"}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Employee Modal */}
      <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Employee Action</DialogTitle>
            <DialogDescription>
              {selectedEmployeeForDelete?.is_active
                ? "This will deactivate the employee."
                : "Employee is inactive. Choose an action."}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-2">
            {selectedEmployeeForDelete?.is_active && (
              <Button variant="destructive" onClick={handleSoftDelete}>
                Deactivate
              </Button>
            )}
            {!selectedEmployeeForDelete?.is_active && (
              <>
                <Button variant="outline" onClick={handleReactivate}>
                  Reactivate
                </Button>
                <Button variant="destructive" onClick={handlePermanentDelete}>
                  Permanent Delete
                </Button>
              </>
            )}
            <Button variant="outline" onClick={() => setDeleteModalOpen(false)}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </div>
  );
}

// ==================== EMPLOYEE FORM COMPONENT ====================

// Inside EmployeeForm component - Complete updated version

function EmployeeForm({
  employee,
  onSubmit,
  currentUserRole,
}: {
  employee?: UserType;
  onSubmit: (data: Partial<UserType>) => void;
  currentUserRole: string;
}) {
  const allowedRoles = ROLE_OPTIONS_BY_LOGIN_ROLE[currentUserRole] || [];
  const [employeeSettings, setSettings] = useState({
    disable_mandatory: false,
    is_active: true,
  });

  const [addDepartmentModal, setAddDepartmentModal] = useState(false);
  const [departmentName, setDepartmentName] = useState("");
  const [activeTab, setActiveTab] = useState<"personal" | "salary" | "summary">("personal");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [addDesignationModal, setAddDesignationModal] = useState(false);
  const [designationName, setDesignationName] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  // Salary calculation states
  const [ctcEnabled, setCtcEnabled] = useState(false);
  const [ctcValue, setCtcValue] = useState(0);
  const [isYearly, setIsYearly] = useState(true);
  const [ptState, setPtState] = useState(employee?.ptState || "karnataka");
  const [lwfState, setLwfState] = useState(employee?.lwfState || "karnataka");
  const [ptApplicable, setPtApplicable] = useState(employee?.ptApplicable ?? true);
  const [lwfApplicable, setLwfApplicable] = useState(employee?.lwfApplicable ?? true);
  const [pfApplicable, setPfApplicable] = useState(employee?.pfApplicable ?? true);
  const [pfBasis, setPfBasis] = useState<"statutory" | "actual">(employee?.pfBasis || "statutory");
  const [esicApplicable, setEsicApplicable] = useState(employee?.esicApplicable ?? true);

  // TDS Declaration state
  const [declaration, setDeclaration] = useState({
    sec80c: employee?.sec80c || 0,
    sec80d: employee?.sec80d || 0,
    sec80ccd1b: employee?.sec80ccd1b || 0,
    hraExempt: employee?.hraExempt || 0,
    homeLoan: employee?.homeLoan || 0,
    ltaExempt: employee?.ltaExempt || 0,
    sec80g: employee?.sec80g || 0,
    npsEmployer: employee?.npsEmployer || 0,
    ptPaid: employee?.ptPaid || 0,
    otherDed: employee?.otherDed || 0,
  });

  // Multiple salary records state
  const [salaryRecords, setSalaryRecords] = useState<any[]>([
    {
      id: Date.now().toString(),
      effectiveDate: employee?.hire_date?.split('T')[0] || new Date().toISOString().split('T')[0],
      status: 'active',
      basicSalary: 0,
      hra: 0,
      conveyance: 0,
      medicalallowance: 0,
      allowance: 0,
      lta: 0,
      others: 0,
      bonusAmount: 0,
      customComponents: [],
      isCurrent: true
    }
  ]);

  const [formData, setFormData] = useState({
    id: employee?.id || "",
    first_name: employee?.first_name || "",
    middle_name: employee?.middle_name || "",
    last_name: employee?.last_name || "",
    gender: employee?.gender || "",
    employeeID: employee?.employeeID || "",
    email: employee?.email || "",
    password: "",
    phone: employee?.phone || "",
    date_of_birth: employee?.date_of_birth || "",
    hire_date: employee?.hire_date || "",
    department_id: employee?.department_id || "",
    // designation_id: employee?.designation_id || "",
    designation_id: "",
    manager_id: employee?.manager_id || "",
    emergency_contacts: employee?.emergency_contacts || "",
    salary_amount: employee?.salary_amount || 0,
    profile_photo_url: employee?.profile_photo_url || "",
    country: employee?.country || "19",
    current_address1: employee?.current_address1 || "",
    current_address2: employee?.current_address2 || "",
    current_city: employee?.current_city || "",
    current_state: employee?.current_state || "",
    current_pin: employee?.current_pin || "",
    permanent_address1: employee?.permanent_address1 || "",
    permanent_address2: employee?.permanent_address2 || "",
    permanent_city: employee?.permanent_city || "",
    permanent_state: employee?.permanent_state || "",
    permanent_pin: employee?.permanent_pin || "",
    employment_status: employee?.employment_status || "",
    sameAs: employee?.sameAs || false,
    accountNum: employee?.accountNum || "",
    ifscCode: employee?.ifscCode || "",
    bankName: employee?.bankName || "",
    bankbranch: employee?.bankbranch || "",
    swiftCode: employee?.swiftCode || "",
    upiCode: employee?.upiCode || "",
    qrcodeUrl: employee?.qrcodeUrl || "",
    othersBankDetails: employee?.othersBankDetails || "",
    is_active: employee?.is_active ?? true,
    department: employee?.department || "",
    role: employee?.role || "employee",
    netsalary: employee?.netsalary || 0,
    leave: employee?.leave || 0,
    modeOfWorking: employee?.modeOfWorking || "in-office",
    workfromhome: employee?.workfromhome || false,
    workfromhome_list: employee?.workfromhome_list || [],
    taxRegime: employee?.taxRegime || "new",
    tdsApplicable: employee?.tdsApplicable ?? true,
    TaxConfig: employee?.TaxConfig || [],

  });

  const isMandatoryDisabled = employeeSettings.disable_mandatory;

  useEffect(() => {
    fetchEmployeeSettings();
  }, []);


  const fetchEmployeeSettings = async () => {
    try {
      const res = await api.get("/get_employee_setting");
      // setEmployeeSettings(res.data);
      setSettings({
        disable_mandatory: res.data?.employee_setting?.disable_mandatory ?? false,
        is_active: res.data?.employee_setting?.is_active ?? true,
      });
      console.log("Employee Settings:", res.data);
    } catch (err) {
      console.error("Error fetching employee settings", err);
    }
  };

  // Load existing salary records from employee
  useEffect(() => {
    if (employee?.SalaryDetails && employee.SalaryDetails.length > 0) {
      const records = employee.SalaryDetails.map((detail: any, index: number) => ({
        id: Date.now().toString() + index,
        effectiveDate: detail.effectiveDate || employee.hire_date?.split('T')[0] || new Date().toISOString().split('T')[0],
        status: detail.status || (index === 0 ? 'active' : 'pending'),
        basicSalary: detail.basicsalary || 0,
        hra: detail.hra || 0,
        conveyance: detail.conveyance || 0,
        medicalallowance: detail.medicalallowance || 0,
        allowance: detail.allowance || 0,
        lta: detail.lta || 0,
        others: detail.others || 0,
        bonusAmount: detail.bonus || 0,
        customComponents: detail.customComponents || [],
        isCurrent: index === 0
      }));
      if (records.length > 0) {
        setSalaryRecords(records);

        // Initialize CTC from first record
        const firstRecord = records[0];
        const details = calculateSalaryDetailsForRecord(firstRecord);
        setCtcValue(Math.round(details.ctc * 12));
      }
    }
  }, [employee]);

  // Update salary record function
  const updateSalaryRecord = useCallback((id: string, field: string, value: any) => {
    setSalaryRecords(prevRecords =>
      prevRecords.map(record =>
        record.id === id ? { ...record, [field]: value } : record
      )
    );
  }, []);

  // Add new salary record
  const addSalaryRecord = () => {
    setSalaryRecords([
      ...salaryRecords,
      {
        id: Date.now().toString(),
        effectiveDate: new Date().toISOString().split('T')[0],
        status: 'pending',
        basicSalary: 0,
        hra: 0,
        conveyance: 0,
        medicalallowance: 0,
        allowance: 0,
        lta: 0,
        others: 0,
        bonusAmount: 0,
        customComponents: [],
        isCurrent: false
      }
    ]);
  };

  // Remove salary record
  const removeSalaryRecord = (id: string) => {
    if (salaryRecords.length === 1) {
      toast({ // title: "Error",
        description: "At least one salary record is required", variant: "destructive"
      });
      return;
    }
    setSalaryRecords(salaryRecords.filter(record => record.id !== id));
  };

  // Calculate salary details for a specific record
  const calculateSalaryDetailsForRecord = useCallback((record: any) => {
    const basic = num(record.basicSalary);
    const hra = num(record.hra);
    const conv = num(record.conveyance);
    const med = num(record.medicalallowance);
    const special = num(record.allowance);
    const lta = num(record.lta);
    const other = num(record.others);
    const bonus = num(record.bonusAmount);

    const customTotal = record.customComponents?.reduce((sum: number, comp: any) => sum + (comp.amount || 0), 0) || 0;
    const gross = basic + hra + conv + med + special + lta + other + bonus + customTotal;

    let pfBase = pfBasis === "statutory" ? Math.min(basic, 15000) : basic;
    const pfEE = pfApplicable ? Math.round(pfBase * 0.12) : 0;
    const pfER = pfApplicable ? Math.round(pfBase * 0.12) : 0;
    const eps = pfApplicable ? Math.round(Math.min(pfBase, 15000) * 0.0833) : 0;
    const edli = pfApplicable ? Math.round(Math.min(pfBase, 15000) * 0.005) : 0;

    const esicEE = (esicApplicable && gross <= 21000) ? Math.round(gross * 0.0075) : 0;
    const esicER = (esicApplicable && gross <= 21000) ? Math.round(gross * 0.0325) : 0;

    const pt = ptApplicable ? calcPT(gross, ptState) : 0;

    const lwfData = lwfMonthly(lwfState);
    const lwfEE = lwfApplicable ? Math.round(lwfData.ee) : 0;
    const lwfER = lwfApplicable ? Math.round(lwfData.er) : 0;

    const grossAnnual = gross * 12;
    const pfEEAnnual = pfEE * 12;
    const tds = formData.tdsApplicable
      ? calcTDS(grossAnnual, formData.taxRegime, declaration, pfEEAnnual)
      : 0;

    const totDed = pfEE + esicEE + pt + lwfEE + tds;
    const net = gross - totDed;
    const ctc = gross + pfER + esicER + eps + edli + lwfER;

    return { gross, pfEE, pfER, eps, edli, esicEE, esicER, pt, lwfEE, lwfER, tds, totDed, net, ctc, customTotal };
  }, [pfApplicable, pfBasis, esicApplicable, ptApplicable, ptState, lwfApplicable, lwfState, declaration, formData.tdsApplicable, formData.taxRegime]);

  // Handle CTC auto-calculation for the first record
  useEffect(() => {
    if (ctcEnabled && ctcValue > 0 && salaryRecords.length > 0) {
      const monthlyCTC = isYearly ? ctcValue / 12 : ctcValue;
      const basic = monthlyCTC * 0.5;
      const hra = basic * 0.5;
      const remaining = monthlyCTC - basic - hra;
      const allowanceCount = 6;
      const perAllowance = remaining / allowanceCount;

      updateSalaryRecord(salaryRecords[0].id, 'basicSalary', Math.round(basic));
      updateSalaryRecord(salaryRecords[0].id, 'hra', Math.round(hra));
      updateSalaryRecord(salaryRecords[0].id, 'conveyance', Math.round(perAllowance));
      updateSalaryRecord(salaryRecords[0].id, 'medicalallowance', Math.round(perAllowance));
      updateSalaryRecord(salaryRecords[0].id, 'allowance', Math.round(perAllowance));
      updateSalaryRecord(salaryRecords[0].id, 'lta', Math.round(perAllowance));
      updateSalaryRecord(salaryRecords[0].id, 'others', Math.round(perAllowance));
      updateSalaryRecord(salaryRecords[0].id, 'bonusAmount', Math.round(perAllowance));
    }
  }, [ctcEnabled, ctcValue, isYearly, salaryRecords.length, updateSalaryRecord]);

  // Calculate CTC from current components
  const calculateCTCFromComponents = useCallback(() => {
    if (salaryRecords.length > 0) {
      const details = calculateSalaryDetailsForRecord(salaryRecords[0]);
      const annualCTC = details.ctc * 12;
      setCtcValue(Math.round(annualCTC));
      setIsYearly(true);
      toast({
        title: "CTC Calculated",
        description: `Annual CTC: ₹${fmt(annualCTC)}`,
      });
    }
  }, [salaryRecords, calculateSalaryDetailsForRecord]);

  useEffect(() => {
    dispatch(getDesignationList());
  }, []);

  useEffect(() => {
    dispatch(getDepartmentList());
  }, []);

  useEffect(() => {
    if (formData.department_id && !["admin", "hr head"].includes(formData.role)) {
      dispatch(getManagerList(formData.department_id));
    }
  }, [formData.department_id, formData.role]);

  useEffect(() => {
    if (formData.sameAs) {
      setFormData((prev) => ({
        ...prev,
        permanent_address1: prev.current_address1,
        permanent_address2: prev.current_address2,
        permanent_city: prev.current_city,
        permanent_state: prev.current_state,
        permanent_pin: prev.current_pin,
      }));
    }
  }, [formData.current_address1, formData.current_address2, formData.current_city, formData.current_state, formData.current_pin, formData.sameAs]);

  useEffect(() => {
    if ((formData.modeOfWorking === "remote" || formData.modeOfWorking === "hybrid") && formData.workfromhome_list.length === 0) {
      setFormData((prev) => ({
        ...prev,
        workfromhome_list: [{
          office_name: "",
          location: "",
          working_hours_start: "",
          working_hours_end: "",
          wifi_ssid: "",
          latitude: "",
          longitude: "",
          radius_meters: 0,
          is_active: true,
        }],
      }));
    }
    if (formData.modeOfWorking === "in-office") {
      setFormData((prev) => ({
        ...prev,
        workfromhome_list: [],
      }));
    }
  }, [formData.modeOfWorking]);

  // useEffect(() => {
  //   const fetchComplianceSettings = async () => {
  //     try {
  //       const response = await getCompanyComplianceSettings();
  //       let settings = null;
  //       if (response && response.data) {
  //         settings = Array.isArray(response.data) ? response.data[0] : response.data;
  //       } else if (Array.isArray(response)) {
  //         settings = response[0];
  //       } else {
  //         settings = response;
  //       }

  //       const isEditing = !!employee?.id;

  //       if (!isEditing) {
  //         setPfApplicable(settings?.pfApplicable === true);
  //         setEsicApplicable(settings?.esicApplicable === true);
  //         setPtApplicable(settings?.ptApplicable === true);
  //         setLwfApplicable(settings?.lwfApplicable === true);
  //         setFormData(prev => ({
  //           ...prev,
  //           tdsApplicable: settings?.tdsApplicable === true,
  //           pfBasis: settings?.pfBasis || "statutory",
  //           taxRegime: settings?.taxRegime || "new",
  //         }));
  //         setPtState(settings?.ptState || "karnataka");
  //         setLwfState(settings?.lwfState || "karnataka");
  //         setDeclaration(prev => ({
  //           ...prev,
  //           sec80c: settings?.sec80c || 0,
  //           sec80d: settings?.sec80d || 0,
  //           sec80ccd1b: settings?.sec80ccd1b || 0,
  //           hraExempt: settings?.hraExempt || 0,
  //           homeLoan: settings?.homeLoan || 0,
  //           ltaExempt: settings?.ltaExempt || 0,
  //           sec80g: settings?.sec80g || 0,
  //           npsEmployer: settings?.npsEmployer || 0,
  //           ptPaid: settings?.ptPaid || 0,
  //           otherDed: settings?.otherDed || 0,
  //         }));
  //       } else {
  //         setPfApplicable(employee?.pfApplicable !== undefined ? employee.pfApplicable : (settings?.pfApplicable === true));
  //         setEsicApplicable(employee?.esicApplicable !== undefined ? employee.esicApplicable : (settings?.esicApplicable === true));
  //         setPtApplicable(employee?.ptApplicable !== undefined ? employee.ptApplicable : (settings?.ptApplicable === true));
  //         setLwfApplicable(employee?.lwfApplicable !== undefined ? employee.lwfApplicable : (settings?.lwfApplicable === true));
  //         setFormData(prev => ({
  //           ...prev,
  //           tdsApplicable: employee?.tdsApplicable !== undefined ? employee.tdsApplicable : (settings?.tdsApplicable === true),
  //           pfBasis: employee?.pfBasis || settings?.pfBasis || "statutory",
  //           taxRegime: employee?.taxRegime || settings?.taxRegime || "new",
  //         }));
  //         setPtState(employee?.ptState || settings?.ptState || "karnataka");
  //         setLwfState(employee?.lwfState || settings?.lwfState || "karnataka");
  //         setDeclaration({
  //           sec80c: employee?.sec80c ?? settings?.sec80c ?? 0,
  //           sec80d: employee?.sec80d ?? settings?.sec80d ?? 0,
  //           sec80ccd1b: employee?.sec80ccd1b ?? settings?.sec80ccd1b ?? 0,
  //           hraExempt: employee?.hraExempt ?? settings?.hraExempt ?? 0,
  //           homeLoan: employee?.homeLoan ?? settings?.homeLoan ?? 0,
  //           ltaExempt: employee?.ltaExempt ?? settings?.ltaExempt ?? 0,
  //           sec80g: employee?.sec80g ?? settings?.sec80g ?? 0,
  //           npsEmployer: employee?.npsEmployer ?? settings?.npsEmployer ?? 0,
  //           ptPaid: employee?.ptPaid ?? settings?.ptPaid ?? 0,
  //           otherDed: employee?.otherDed ?? settings?.otherDed ?? 0,
  //         });
  //       }
  //     } catch (error) {
  //       console.error("Error fetching compliance settings:", error);
  //       if (employee?.id) {
  //         setPfApplicable(employee?.pfApplicable ?? true);
  //         setEsicApplicable(employee?.esicApplicable ?? true);
  //         setPtApplicable(employee?.ptApplicable ?? true);
  //         setLwfApplicable(employee?.lwfApplicable ?? true);
  //         setFormData(prev => ({
  //           ...prev,
  //           tdsApplicable: employee?.tdsApplicable ?? true,
  //           pfBasis: employee?.pfBasis || "statutory",
  //           taxRegime: employee?.taxRegime || "new",
  //         }));
  //       }
  //     }
  //   };

  //   fetchComplianceSettings();
  // }, [employee?.id]);

  useEffect(() => {
  // 🔥 STOP API on edit
  if (employee?.id) return;

  const fetchComplianceSettings = async () => {
    try {
      const response = await getCompanyComplianceSettings();

      let settings = null;
      if (response?.data) {
        settings = Array.isArray(response.data) ? response.data[0] : response.data;
      } else if (Array.isArray(response)) {
        settings = response[0];
      } else {
        settings = response;
      }

      // ✅ sirf ADD case
      setPfApplicable(settings?.pfApplicable === true);
      setEsicApplicable(settings?.esicApplicable === true);
      setPtApplicable(settings?.ptApplicable === true);
      setLwfApplicable(settings?.lwfApplicable === true);

      setFormData(prev => ({
        ...prev,
        tdsApplicable: settings?.tdsApplicable === true,
        pfBasis: settings?.pfBasis || "statutory",
        taxRegime: settings?.taxRegime || "new",
      }));

      setPtState(settings?.ptState || "karnataka");
      setLwfState(settings?.lwfState || "karnataka");

      setDeclaration(prev => ({
        ...prev,
        sec80c: settings?.sec80c || 0,
        sec80d: settings?.sec80d || 0,
        sec80ccd1b: settings?.sec80ccd1b || 0,
        hraExempt: settings?.hraExempt || 0,
        homeLoan: settings?.homeLoan || 0,
        ltaExempt: settings?.ltaExempt || 0,
        sec80g: settings?.sec80g || 0,
        npsEmployer: settings?.npsEmployer || 0,
        ptPaid: settings?.ptPaid || 0,
        otherDed: settings?.otherDed || 0,
      }));

    } catch (error) {
      console.error("Error fetching compliance settings:", error);
    }
  };

  fetchComplianceSettings();

}, [employee?.id]);



 useEffect(() => {
  if (!employee?.TaxConfig?.length) return;

  const tax = employee.TaxConfig[0]; 

  setPfApplicable(!!tax.pfApplicable);
  setEsicApplicable(!!tax.esicApplicable);
  setPtApplicable(!!tax.ptApplicable);
  setLwfApplicable(!!tax.lwfApplicable);

  setFormData(prev => ({
    ...prev,
    tdsApplicable: !!tax.tdsApplicable,
  }));

}, [employee]);

  const showReportingPerson = formData.department_id && !["admin", "hr head"].includes(formData.role);
  const managerList = useSelector((state: any) => state.employee.managerList);
  const departmentList = useSelector((state: any) => state.employee.departmentList);
  const designationList = useSelector((state: any) => state.employee.designationList);

  const filteredManagerList = managerList.filter((user: any) => {
    if (formData.role === "manager") return user.role === "hr head";
    if (formData.role === "supervisor") return ["hr head", "manager"].includes(user.role);
    if (formData.role === "employee") return ["hr head", "manager", "supervisor"].includes(user.role);
    if (formData.role === "director") return ["hr head", "manager", "supervisor"].includes(user.role);
    if (formData.role === "founder") return ["hr head", "manager", "supervisor"].includes(user.role);
    return false;
  });

  const handleAddDepartment = async () => {
    await api.post("/add_department", { name: departmentName });
    setDepartmentName("");
    setAddDepartmentModal(false);
    dispatch(getDepartmentList());
  };

  const handleAddDesignation = async () => {
    await api.post("/add_designation", { name: designationName });
    setDesignationName("");
    setAddDesignationModal(false);
    dispatch(getDesignationList());
  };

  const { toast } = useToast();

  const REQUIRED_PERSONAL_FIELDS = [
    { key: 'first_name', label: 'First Name' },
    { key: 'last_name', label: 'Last Name' },
    { key: 'employeeID', label: 'Employee ID' },
    { key: 'email', label: 'Email Address' },
    { key: 'gender', label: 'Gender' },
    { key: 'phone', label: 'Phone Number' },
    { key: 'department_id', label: 'Department' },
    { key: 'role', label: 'Role' },
    { key: 'designation_id', label: 'Designation' },
    { key: 'hire_date', label: 'Date of Joining' },
    { key: 'country', label: 'Country' },
    { key: 'current_address1', label: 'Current Address Line 1' },
    { key: 'current_city', label: 'Current City' },
    { key: 'current_state', label: 'Current State' },
    { key: 'current_pin', label: 'Current Pincode' },
  ];

  const getRequiredFields = () => {
    const fields = [...REQUIRED_PERSONAL_FIELDS];
    if (!employee?.id) {
      fields.push({ key: 'password', label: 'Password' });
    }
    return fields;
  };

  // const validatePersonalDetails = (): string[] => {
  //   if (isMandatoryDisabled) {
  //   return [];
  // }
  //   const missingFields: string[] = [];
  //   const requiredFields = getRequiredFields();

  //   for (const field of requiredFields) {
  //     const value = formData[field.key as keyof typeof formData];
  //     if (!isMandatoryDisabled && (!value || (typeof value === 'string' && value.trim() === ''))) {
  //       missingFields.push(field.label);
  //     }
  //   }

  //   if (formData.phone && formData.phone.length !== 10) {
  //     missingFields.push('Phone Number (must be 10 digits)');
  //   }

  //   const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  //   if (formData.email && !emailRegex.test(formData.email)) {
  //     missingFields.push('Valid Email Address');
  //   }

  //   if (formData.current_pin && !/^\d{6}$/.test(formData.current_pin)) {
  //     missingFields.push('Current Pincode (must be 6 digits)');
  //   }

  //   return missingFields;
  // };

  // const handleNext = () => {
  //   if (activeTab === "personal") {
  //     const missingFields = validatePersonalDetails();
  //     if (missingFields.length > 0) {
  //       toast({
  //         title: "Missing Required Fields",
  //         description: `Please fill in: ${missingFields.join(", ")}`,
  //         variant: "destructive",
  //       });
  //       return;
  //     }
  //     setActiveTab("salary");
  //   } else if (activeTab === "salary") {
  //     setActiveTab("summary");
  //   }
  // };


  const validatePersonalDetails = (): string[] => {

    // ✅ ADD THIS LINE (FULL SKIP)
    if (isMandatoryDisabled) {
      return [];
    }

    const missingFields: string[] = [];
    const requiredFields = getRequiredFields();

    for (const field of requiredFields) {
      const value = formData[field.key as keyof typeof formData];

      if (!value || (typeof value === 'string' && value.trim() === '')) {
        missingFields.push(field.label);
      }
    }

    // optional validations (only if value exists)
    if (formData.phone && formData.phone.length !== 10) {
      missingFields.push('Phone Number (must be 10 digits)');
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.email && !emailRegex.test(formData.email)) {
      missingFields.push('Valid Email Address');
    }

    if (formData.current_pin && !/^\d{6}$/.test(formData.current_pin)) {
      missingFields.push('Current Pincode (must be 6 digits)');
    }

    return missingFields;
  };

  const handleNext = () => {
    if (activeTab === "personal") {

      // ✅ agar mandatory disabled hai → skip validation
      if (isMandatoryDisabled) {
        setActiveTab("salary");
        return;
      }

      const missingFields = validatePersonalDetails();

      if (missingFields.length > 0) {
        toast({
          title: "Missing Required Fields",
          description: `Please fill in: ${missingFields.join(", ")}`,
          variant: "destructive",
        });
        return;
      }

      setActiveTab("salary");
    } else if (activeTab === "salary") {
      setActiveTab("summary");
    }
  };
  const handleBack = () => {
    if (activeTab === "salary") {
      setActiveTab("personal");
    } else if (activeTab === "summary") {
      setActiveTab("salary");
    }
  };

 const buildTaxConfig = () => {
  return [
    {
      pfApplicable: pfApplicable,
      esicApplicable: esicApplicable,
      ptApplicable: ptApplicable,
      lwfApplicable: lwfApplicable,
      tdsApplicable: formData.tdsApplicable,
    },
  ];
};

  const handleFinalSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isMandatoryDisabled) {
      const missingFields = validatePersonalDetails();

      if (missingFields.length > 0) {
        toast({
          title: "Missing Required Fields",
          description: `Please fill in: ${missingFields.join(", ")}`,
          variant: "destructive",
        });
        return;
      }
    }
    setIsSubmitting(true);
    try {
      const salaryDetailsArray = salaryRecords.map(record => {
        const details = calculateSalaryDetailsForRecord(record);
        return {
          basicsalary: record.basicSalary,
          hra: record.hra,
          conveyance: record.conveyance,
          medicalallowance: record.medicalallowance,
          allowance: record.allowance,
          lta: record.lta,
          others: record.others,
          bonus: record.bonusAmount,
          pf: details.pfEE,
          esi: details.esicEE,
          proftax: details.pt,
          labourwelfare: details.lwfEE,
          tds: details.tds,
          netsalary: details.net,
          effectiveDate: record.effectiveDate,
          status: record.status,
          customComponents: record.customComponents
        };
      });

      const finalData = {
        ...formData,
        designation: getDesignationName(formData.designation_id),
        SalaryDetails: salaryDetailsArray,
        TaxConfig: buildTaxConfig(),
        basicsalary: salaryRecords[0]?.basicSalary || 0,
        hra: salaryRecords[0]?.hra || 0,
        conveyance: salaryRecords[0]?.conveyance || 0,
        medicalallowance: salaryRecords[0]?.medicalallowance || 0,
        allowance: salaryRecords[0]?.allowance || 0,
        lta: salaryRecords[0]?.lta || 0,
        others: salaryRecords[0]?.others || 0,
        bonus: salaryRecords[0]?.bonusAmount || 0,
        netsalary: calculateSalaryDetailsForRecord(salaryRecords[0]).net,
        salary_amount: calculateSalaryDetailsForRecord(salaryRecords[0]).gross,
        pfApplicable,
        esicApplicable,
        ptApplicable,
        lwfApplicable,
        pfBasis,
        ptState,
        lwfState,
        tds: calculateSalaryDetailsForRecord(salaryRecords[0]).tds,
        ...declaration,
      };

      await onSubmit(finalData);
    } catch (error) {
      console.error("Error submitting form:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getDepartmentName = (deptId: string) => {
    const dept = departmentList.find((d: any) => d.id === deptId);
    return dept?.name || deptId;
  };

  const getDesignationName = (designationId: string) => {
    const designation = designationList.find((d: any) => d.id === designationId);
    return designation?.name || designationId;
  };

  const getStateName = (stateId: string) => {
    const stateObj = state.find((s) => String(s.id) === stateId);
    return stateObj?.label || stateId;
  };

  const getCountryName = (countryId: string) => {
    const countryObj = country.find((c) => String(c.id) === countryId);
    return countryObj?.label || countryId;
  };

  const getManagerName = (managerId: string) => {
    const manager = managerList.find((m: any) => m.id === managerId);
    return manager?.name || managerId;
  };

  const isNewRegime = formData.taxRegime === "new";

  useEffect(() => {
    if (employee && designationList.length > 0) {
      const matchedDesignation = designationList.find(
        (d: any) => d.name === employee.designation
      );

      if (matchedDesignation) {
        setFormData((prev) => ({
          ...prev,
          designation_id: matchedDesignation.id,
        }));
      }
    }
  }, [employee, designationList]);

  return (
    <form onSubmit={handleFinalSubmit} className="space-y-6" >
      {/* Tab Navigation */}
      <div className="flex border-b border-gray-200">
        <button
          type="button"
          onClick={() => setActiveTab("personal")}
          className={`px-6 py-3 text-sm font-medium transition-all ${activeTab === "personal"
            ? "border-b-2 border-blue-500 text-blue-600"
            : "text-gray-500 hover:text-gray-700"
            }`}
        >
          <span className="inline-flex items-center gap-2">
            <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs">1</span>
            Personal Details
          </span>
        </button>
        <button
          type="button"
          onClick={() => setActiveTab("salary")}
          className={`px-6 py-3 text-sm font-medium transition-all ${activeTab === "salary"
            ? "border-b-2 border-blue-500 text-blue-600"
            : "text-gray-500 hover:text-gray-700"
            }`}
        >
          <span className="inline-flex items-center gap-2">
            <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs">2</span>
            Salary & Deductions
          </span>
        </button>
        <button
          type="button"
          onClick={() => setActiveTab("summary")}
          className={`px-6 py-3 text-sm font-medium transition-all ${activeTab === "summary"
            ? "border-b-2 border-blue-500 text-blue-600"
            : "text-gray-500 hover:text-gray-700"
            }`}
        >
          <span className="inline-flex items-center gap-2">
            <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs">3</span>
            Review & Submit
          </span>
        </button>
      </div>

      {/* Personal Details Tab */}
      {activeTab === "personal" && (
        <div className="space-y-6">
          {/* Personal Information Card */}
          <Card className="border-0 shadow-md overflow-hidden">
            <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-blue-100">
              <CardTitle className="text-lg flex items-center gap-2">
                <User className="h-5 w-5 text-blue-600" />
                Personal Information
              </CardTitle>
              <CardDescription>Basic personal and contact details</CardDescription>
            </CardHeader>
            <CardContent className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                <div>
                  <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">First Name</Label>
                  <Input
                    value={formData.first_name}
                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                    required={!isMandatoryDisabled}
                    className="mt-1.5 font-medium"
                    placeholder="Enter first name"
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Middle Name</Label>
                  <Input
                    value={formData.middle_name}
                    onChange={(e) => setFormData({ ...formData, middle_name: e.target.value })}
                    required={!isMandatoryDisabled}
                    className="mt-1.5"
                    placeholder="Enter middle name"
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Last Name</Label>
                  <Input
                    value={formData.last_name}
                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                    required={!isMandatoryDisabled}
                    className="mt-1.5 font-medium"
                    placeholder="Enter last name"
                  />
                </div>

                <div>
                  <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Gender
                  </Label>

                  <Select
                    value={formData.gender || ""}
                    onValueChange={(value) =>
                      setFormData((prev) => ({
                        ...prev,
                        gender: value,
                      }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select Gender" />
                    </SelectTrigger>

                    <SelectContent>
                      <SelectItem value="male">Male</SelectItem>
                      <SelectItem value="female">Female</SelectItem>
                      <SelectItem value="others">Others</SelectItem>
                      
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Employee ID</Label>
                  <Input
                    value={formData.employeeID}
                    onChange={(e) => setFormData({ ...formData, employeeID: e.target.value })}
                    required={!isMandatoryDisabled}
                    className="mt-1.5 font-mono"
                    placeholder="EMP001"
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    <Mail className="h-3 w-3 inline mr-1" />
                    Email Address
                  </Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required={!isMandatoryDisabled}
                    className="mt-1.5"
                    placeholder="employee@company.com"
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    <Phone className="h-3 w-3 inline mr-1" />
                    Phone Number
                  </Label>
                  <Input
                    type="text"
                    inputMode="numeric"
                    maxLength={10}
                    placeholder="Enter 10 digit phone number"
                    value={formData.phone}
                    onWheel={(e) => e.currentTarget.blur()}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, "");
                      if (value.length <= 10) {
                        setFormData({ ...formData, phone: value });
                      }
                    }}
                    className="mt-1.5"
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    <Building2 className="h-3 w-3 inline mr-1" />
                    Department
                  </Label>
                  <Select
                    value={formData.department_id}
                    onValueChange={(value) => setFormData({ ...formData, department_id: value })}
                  >
                    <SelectTrigger className="mt-1.5">
                      <SelectValue placeholder="Select department" />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      {departmentList.map((dept: any) => (
                        <SelectItem key={dept.id} value={dept.id}>
                          {dept.name}
                        </SelectItem>
                      ))}
                      <div className="px-2 py-1.5 border-t mt-1">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="w-full"
                          onClick={() => setAddDepartmentModal(true)}
                        >
                          <Plus className="h-3 w-3 mr-1" />
                          Add Department
                        </Button>
                      </div>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    <User className="h-3 w-3 inline mr-1" />
                    Role
                  </Label>
                  <Select
                    value={formData.role}
                    onValueChange={(value) => setFormData({ ...formData, role: value })}
                  >
                    <SelectTrigger className="mt-1.5">
                      <SelectValue placeholder="Select role" />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      {allowedRoles.map((role) => (
                        <SelectItem key={role} value={role}>
                          <span className="capitalize">{role.toUpperCase()}</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    <FileText className="h-3 w-3 inline mr-1" />
                    Designation
                  </Label>
                  <Select
                    value={formData.designation_id}
                    onValueChange={(value) =>
                      setFormData({ ...formData, designation_id: value })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select Designation" />
                    </SelectTrigger>

                    <SelectContent>
                      {designationList.map((d: any) => (
                        <SelectItem key={d.id} value={d.id}>
                          {d.name}
                        </SelectItem>
                      ))}
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="w-full"
                        onClick={() => setAddDesignationModal(true)}
                      >
                        <Plus className="h-3 w-3 mr-1" />
                        Add Designation
                      </Button>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    <Calendar className="h-3 w-3 inline mr-1" />
                    Date of Joining
                  </Label>
                  <Input
                    type="date"
                    value={formData.hire_date}
                    onChange={(e) => setFormData({ ...formData, hire_date: e.target.value })}
                    required={!isMandatoryDisabled}
                    className="mt-1.5"
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    <Calendar className="h-3 w-3 inline mr-1" />
                    Date of Birth
                  </Label>
                  <Input
                    type="date"
                    value={formData.date_of_birth || ""}
                    onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })}
                    className="mt-1.5"
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    <Building2 className="h-3 w-3 inline mr-1" />
                    Country
                  </Label>
                  <Select
                    value={formData.country}
                    onValueChange={(value) => setFormData({ ...formData, country: value })}
                  >
                    <SelectTrigger className="mt-1.5">
                      <SelectValue placeholder="Select Country" />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      {country.map((e) => (
                        <SelectItem key={e.id} value={String(e.id)}>
                          {e.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    <Phone className="h-3 w-3 inline mr-1" />
                    Emergency Contact
                  </Label>
                  <Input
                    type="number"
                    onWheel={(e) => e.currentTarget.blur()}
                    value={formData.emergency_contacts}
                    onChange={(e) => setFormData({ ...formData, emergency_contacts: e.target.value })}
                    className="mt-1.5"
                    placeholder="Emergency contact number"
                  />
                </div>
                <div className="relative">
                  <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Password
                  </Label>
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="mt-1.5 pr-10"
                    placeholder="Leave blank to keep same password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-[38px] text-gray-500"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              {showReportingPerson && (
                <div className="mt-6 pt-4 border-t border-gray-200">
                  <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    <User className="h-3 w-3 inline mr-1" />
                    Reporting Person
                  </Label>
                  <Select
                    value={formData.manager_id}
                    onValueChange={(value) => setFormData({ ...formData, manager_id: value })}
                  >
                    <SelectTrigger className="mt-1.5">
                      <SelectValue placeholder="Select Reporting Person" />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      {filteredManagerList.map((e: any) => (
                        <SelectItem key={e.id} value={e.id}>
                          {e.name} <span className="text-xs text-gray-500">({e.role.toUpperCase()})</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Current Address Card */}
          <Card className="border-0 shadow-md overflow-hidden">
            <CardHeader className="bg-gradient-to-r from-green-50 to-emerald-50 border-b border-green-100">
              <CardTitle className="text-lg flex items-center gap-2">
                <MapPin className="h-5 w-5 text-green-600" />
                Current Address
              </CardTitle>
              <CardDescription>Residential address details</CardDescription>
            </CardHeader>
            <CardContent className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                <div className="lg:col-span-2">
                  <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Address Line 1</Label>
                  <Input
                    value={formData.current_address1}
                    onChange={(e) => setFormData({ ...formData, current_address1: e.target.value })}
                    required={!isMandatoryDisabled}
                    className="mt-1.5"
                    placeholder="House number, building name"
                  />
                </div>
                <div className="lg:col-span-2">
                  <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Address Line 2</Label>
                  <Input
                    value={formData.current_address2}
                    onChange={(e) => setFormData({ ...formData, current_address2: e.target.value })}
                    className="mt-1.5"
                    placeholder="Street, area, landmark"
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">City</Label>
                  <Input
                    value={formData.current_city}
                    onChange={(e) => setFormData({ ...formData, current_city: e.target.value })}
                    required={!isMandatoryDisabled}
                    className="mt-1.5"
                    placeholder="Enter city"
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">State</Label>
                  <Select
                    value={formData.current_state}
                    onValueChange={(value) => setFormData({ ...formData, current_state: value })}
                  >
                    <SelectTrigger className="mt-1.5">
                      <SelectValue placeholder="Select State" />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      {state.map((e) => (
                        <SelectItem key={e.id} value={String(e.id)}>
                          {e.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Pincode</Label>
                  <Input
                    type="number"
                    onWheel={(e) => e.currentTarget.blur()}
                    value={formData.current_pin}
                    onChange={(e) => setFormData({ ...formData, current_pin: e.target.value })}
                    required={!isMandatoryDisabled}
                    className="mt-1.5"
                    placeholder="6 digit code"
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Mode of Working</Label>
                  <Select
                    value={formData.modeOfWorking}
                    onValueChange={(value) => {
                      setFormData({
                        ...formData,
                        modeOfWorking: value,
                        workfromhome: value === "remote" || value === "hybrid",
                      });
                    }}
                  >
                    <SelectTrigger className="mt-1.5">
                      <SelectValue placeholder="Select mode" />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="in-office">🏢 In Office</SelectItem>
                      <SelectItem value="remote">🏠 Remote</SelectItem>
                      <SelectItem value="hybrid">🔄 Hybrid</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Permanent Address Card */}
          <Card className="border-0 shadow-md overflow-hidden">
            <CardHeader className="bg-gradient-to-r from-purple-50 to-pink-50 border-b border-purple-100">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <MapPin className="h-5 w-5 text-purple-600" />
                  <CardTitle>Permanent Address</CardTitle>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="sameAs"
                    checked={formData.sameAs}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        setFormData({
                          ...formData,
                          sameAs: true,
                          permanent_address1: formData.current_address1,
                          permanent_address2: formData.current_address2,
                          permanent_city: formData.current_city,
                          permanent_state: formData.current_state,
                          permanent_pin: formData.current_pin,
                        });
                      } else {
                        setFormData({
                          ...formData,
                          sameAs: false,
                          permanent_address1: "",
                          permanent_address2: "",
                          permanent_city: "",
                          permanent_state: "",
                          permanent_pin: "",
                        });
                      }
                    }}
                  />
                  <Label htmlFor="sameAs" className="text-sm cursor-pointer">
                    Same as current address
                  </Label>
                </div>
              </div>
              <CardDescription>Permanent or correspondence address</CardDescription>
            </CardHeader>
            <CardContent className="p-6">
              {formData.sameAs === false && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                  <div className="lg:col-span-2">
                    <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Address Line 1</Label>
                    <Input
                      value={formData.permanent_address1}
                      onChange={(e) => setFormData({ ...formData, permanent_address1: e.target.value })}
                      className="mt-1.5"
                      placeholder="House number, building name"
                    />
                  </div>
                  <div className="lg:col-span-2">
                    <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Address Line 2</Label>
                    <Input
                      value={formData.permanent_address2}
                      onChange={(e) => setFormData({ ...formData, permanent_address2: e.target.value })}
                      className="mt-1.5"
                      placeholder="Street, area, landmark"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">City</Label>
                    <Input
                      value={formData.permanent_city}
                      onChange={(e) => setFormData({ ...formData, permanent_city: e.target.value })}
                      className="mt-1.5"
                      placeholder="Enter city"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">State</Label>
                    <Select
                      value={formData.permanent_state}
                      onValueChange={(value) => setFormData({ ...formData, permanent_state: value })}
                    >
                      <SelectTrigger className="mt-1.5">
                        <SelectValue placeholder="Select State" />
                      </SelectTrigger>
                      <SelectContent className="bg-white">
                        {state.map((e) => (
                          <SelectItem key={e.id} value={String(e.id)}>
                            {e.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Pincode</Label>
                    <Input
                      value={formData.permanent_pin}
                      onChange={(e) => setFormData({ ...formData, permanent_pin: e.target.value })}
                      className="mt-1.5"
                      placeholder="6 digit code"
                    />
                  </div>
                </div>
              )}
              {formData.sameAs && (
                <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg">
                  <MapPin className="h-12 w-12 mx-auto text-gray-400 mb-2" />
                  <p>Permanent address is same as current address</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Work From Home Setup Card */}
          {(formData.modeOfWorking === "remote" || formData.modeOfWorking === "hybrid") && (
            <Card className="border-0 shadow-md overflow-hidden">
              <CardHeader className="bg-gradient-to-r from-orange-50 to-amber-50 border-b border-orange-100">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Building2 className="h-5 w-5 text-orange-600" />
                  Work From Home / Remote Setup
                </CardTitle>
                <CardDescription>Configure remote work locations and details</CardDescription>
              </CardHeader>
              <CardContent className="p-6 space-y-4">
                {formData.workfromhome_list.map((item: any, index: number) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex justify-between items-center mb-3">
                      <h4 className="font-semibold text-gray-700">Location #{index + 1}</h4>
                      {index > 0 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="text-red-500 hover:text-red-700"
                          onClick={() => {
                            const list = [...formData.workfromhome_list];
                            list.splice(index, 1);
                            setFormData({ ...formData, workfromhome_list: list });
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                      <div>
                        <Label className="text-xs font-semibold text-gray-500">Office Name</Label>
                        <Input
                          value={item.office_name}
                          onChange={(e) => {
                            const list = [...formData.workfromhome_list];
                            list[index].office_name = e.target.value;
                            setFormData({ ...formData, workfromhome_list: list });
                          }}
                          className="mt-1"
                          placeholder="Office/Branch name"
                        />
                      </div>
                      <div>
                        <Label className="text-xs font-semibold text-gray-500">Location</Label>
                        <Input
                          value={item.location}
                          onChange={(e) => {
                            const list = [...formData.workfromhome_list];
                            list[index].location = e.target.value;
                            setFormData({ ...formData, workfromhome_list: list });
                          }}
                          className="mt-1"
                          placeholder="City, Area"
                        />
                      </div>
                      <div>
                        <Label className="text-xs font-semibold text-gray-500">Working Hours</Label>
                        <div className="flex gap-2 mt-1">
                          <Input
                            type="time"
                            value={item.working_hours_start}
                            onChange={(e) => {
                              const list = [...formData.workfromhome_list];
                              list[index].working_hours_start = e.target.value;
                              setFormData({ ...formData, workfromhome_list: list });
                            }}
                            className="w-full"
                            placeholder="Start"
                          />
                          <span className="self-center">to</span>
                          <Input
                            type="time"
                            value={item.working_hours_end}
                            onChange={(e) => {
                              const list = [...formData.workfromhome_list];
                              list[index].working_hours_end = e.target.value;
                              setFormData({ ...formData, workfromhome_list: list });
                            }}
                            className="w-full"
                            placeholder="End"
                          />
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs font-semibold text-gray-500">WiFi SSID</Label>
                        <Input
                          value={item.wifi_ssid}
                          onChange={(e) => {
                            const list = [...formData.workfromhome_list];
                            list[index].wifi_ssid = e.target.value;
                            setFormData({ ...formData, workfromhome_list: list });
                          }}
                          className="mt-1"
                          placeholder="Network name"
                        />
                      </div>
                      <div>
                        <Label className="text-xs font-semibold text-gray-500">Latitude</Label>
                        <Input
                          type="number"
                          step="any"
                          value={item.latitude}
                          onChange={(e) => {
                            const list = [...formData.workfromhome_list];
                            list[index].latitude = e.target.value;
                            setFormData({ ...formData, workfromhome_list: list });
                          }}
                          className="mt-1"
                          placeholder="Latitude"
                        />
                      </div>
                      <div>
                        <Label className="text-xs font-semibold text-gray-500">Longitude</Label>
                        <Input
                          type="number"
                          step="any"
                          value={item.longitude}
                          onChange={(e) => {
                            const list = [...formData.workfromhome_list];
                            list[index].longitude = e.target.value;
                            setFormData({ ...formData, workfromhome_list: list });
                          }}
                          className="mt-1"
                          placeholder="Longitude"
                        />
                      </div>
                      <div>
                        <Label className="text-xs font-semibold text-gray-500">Radius (meters)</Label>
                        <Input
                          type="number"
                          step="any"
                          value={item.radius_meters}
                          onChange={(e) => {
                            const list = [...formData.workfromhome_list];
                            list[index].radius_meters = e.target.value === "" ? 0 : parseFloat(e.target.value);
                            setFormData({ ...formData, workfromhome_list: list });
                          }}
                          className="mt-1"
                          placeholder="Geofence radius"
                        />
                      </div>
                    </div>
                  </div>
                ))}
                {formData.workfromhome_list.length < 3 && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setFormData({
                        ...formData,
                        workfromhome_list: [
                          ...formData.workfromhome_list,
                          {
                            office_name: "",
                            location: "",
                            working_hours_start: "",
                            working_hours_end: "",
                            wifi_ssid: "",
                            latitude: "",
                            longitude: "",
                            radius_meters: 0,
                            is_active: true,
                          },
                        ],
                      });
                    }}
                    className="w-full border-dashed"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Another Work Location
                  </Button>
                )}
              </CardContent>
            </Card>
          )}

          <div className="flex justify-end pt-4">
            <Button
              type="button"
              onClick={handleNext}
              className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700"
            >
              Next: Salary & Deductions
              <ChevronRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </div>
      )}

      {/* Salary & Deductions Tab with Multiple Records */}
      {activeTab === "salary" && (
        <div className="space-y-6">
          {/* CTC Toggle and Tax Regime */}
          <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-4 rounded-lg border border-blue-100">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center gap-4 flex-wrap">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={ctcEnabled}
                    onChange={() => setCtcEnabled(!ctcEnabled)}
                    className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                  />
                  <label className="font-medium text-gray-700">Enable CTC Auto-Calculation</label>
                </div>
                {ctcEnabled && (
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      placeholder="Enter CTC"
                      value={ctcValue}
                      onChange={(e) => setCtcValue(Number(e.target.value))}
                      className="w-36"
                    />
                    <Select
                      value={isYearly ? "yearly" : "monthly"}
                      onValueChange={(val) => setIsYearly(val === "yearly")}
                    >
                      <SelectTrigger className="w-24">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="yearly">Yearly</SelectItem>
                        <SelectItem value="monthly">Monthly</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {!ctcEnabled && salaryRecords[0] && (
                  <>
                    <div className="text-xs text-gray-500 ml-2">
                      Current CTC: ₹{fmt(calculateSalaryDetailsForRecord(salaryRecords[0]).ctc * 12)}/year
                    </div>
                    {/* <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={calculateCTCFromComponents}
                      className="ml-2"
                    >
                      Calculate CTC from Components
                    </Button> */}
                  </>
                )}
              </div>
              <div className="flex items-center gap-4 justify-end">
                <label className="font-medium text-gray-700">Tax Regime:</label>
                <Select
                  value={formData.taxRegime}
                  onValueChange={(value) => setFormData({ ...formData, taxRegime: value })}
                >
                  <SelectTrigger className="w-36">
                    <SelectValue placeholder="Select Regime" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="new">New Regime (Default)</SelectItem>
                    <SelectItem value="old">Old Regime</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Multiple Salary Records Cards */}
          {salaryRecords.map((record, index) => {
            const salaryDetails = calculateSalaryDetailsForRecord(record);
            const isCurrentRecord = index === 0;

            return (
              <Card key={record.id} className={`border-0 shadow-md relative ${!isCurrentRecord ? 'opacity-95' : ''}`}>
                {/* Record Header with Date and Status */}
                <div className={`px-6 py-3 rounded-t-xl flex justify-between items-center ${isCurrentRecord ? 'bg-gradient-to-r from-green-50 to-emerald-50' : 'bg-gradient-to-r from-blue-50 to-indigo-50'} border-b`}>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <div className={`w-3 h-3 rounded-full ${record.status === 'active' ? 'bg-green-500' : record.status === 'pending' ? 'bg-yellow-500' : 'bg-gray-500'}`}></div>
                      <span className="text-sm font-semibold text-gray-700">
                        Salary Component {index + 1}
                        {isCurrentRecord && <span className="ml-2 text-xs text-green-600">(Current)</span>}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Label className="text-xs text-gray-500">Effective Date:</Label>
                      <Input
                        type="date"
                        value={record.effectiveDate}
                        onChange={(e) => updateSalaryRecord(record.id, 'effectiveDate', e.target.value)}
                        className="h-8 w-40 text-sm"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <Label className="text-xs text-gray-500">Status:</Label>
                      <Select
                        value={record.status}
                        onValueChange={(value) => updateSalaryRecord(record.id, 'status', value)}
                      >
                        <SelectTrigger className="h-8 w-32 text-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="active">Active</SelectItem>
                          <SelectItem value="pending">Pending</SelectItem>
                          <SelectItem value="archived">Archived</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  {salaryRecords.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeSalaryRecord(record.id)}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>

                <CardContent className="p-6">
                  {/* Add Custom Component Button for each record */}
                  <div className="flex justify-end mb-4">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const updatedRecords = [...salaryRecords];
                        const recordIndex = updatedRecords.findIndex(r => r.id === record.id);
                        updatedRecords[recordIndex].customComponents = [
                          ...(updatedRecords[recordIndex].customComponents || []),
                          { id: Date.now().toString(), name: "", amount: 0 }
                        ];
                        setSalaryRecords(updatedRecords);
                      }}
                      className="border-green-300 text-green-600 hover:bg-green-50"
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Add Component
                    </Button>
                  </div>

                  {/* Standard Components Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div>
                      <Label className="text-xs font-semibold text-gray-500">Basic Salary *</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={record.basicSalary}
                        onChange={(e) => updateSalaryRecord(record.id, 'basicSalary', Number(e.target.value))}
                        className="mt-1"
                        placeholder="0.00"
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-semibold text-gray-500">HRA (House Rent Allowance)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={record.hra}
                        onChange={(e) => updateSalaryRecord(record.id, 'hra', Number(e.target.value))}
                        className="mt-1"
                        placeholder="0.00"
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-semibold text-gray-500">Conveyance Allowance</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={record.conveyance}
                        onChange={(e) => updateSalaryRecord(record.id, 'conveyance', Number(e.target.value))}
                        className="mt-1"
                        placeholder="0.00"
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-semibold text-gray-500">Medical Allowance</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={record.medicalallowance}
                        onChange={(e) => updateSalaryRecord(record.id, 'medicalallowance', Number(e.target.value))}
                        className="mt-1"
                        placeholder="0.00"
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-semibold text-gray-500">Special Allowance</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={record.allowance}
                        onChange={(e) => updateSalaryRecord(record.id, 'allowance', Number(e.target.value))}
                        className="mt-1"
                        placeholder="0.00"
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-semibold text-gray-500">LTA (Leave Travel Allowance)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={record.lta}
                        onChange={(e) => updateSalaryRecord(record.id, 'lta', Number(e.target.value))}
                        className="mt-1"
                        placeholder="0.00"
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-semibold text-gray-500">Other Allowances</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={record.others}
                        onChange={(e) => updateSalaryRecord(record.id, 'others', Number(e.target.value))}
                        className="mt-1"
                        placeholder="0.00"
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-semibold text-gray-500">Bonus / Incentive</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={record.bonusAmount}
                        onChange={(e) => updateSalaryRecord(record.id, 'bonusAmount', Number(e.target.value))}
                        className="mt-1"
                        placeholder="0.00"
                      />
                    </div>
                  </div>

                  {/* Custom Components Section for this record */}
                  {(record.customComponents && record.customComponents.length > 0) && (
                    <div className="mt-6 pt-4 border-t border-gray-200">
                      <div className="flex items-center justify-between mb-3">
                        <Label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                          <Plus className="h-4 w-4 text-green-600" />
                          Custom Components
                        </Label>
                        <span className="text-xs text-gray-500">Click on component name to edit</span>
                      </div>
                      <div className="space-y-3">
                        {record.customComponents.map((component: any, compIndex: number) => (
                          <div key={component.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200 hover:shadow-sm transition-shadow">
                            <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-3">
                              <div>
                                <Label className="text-xs font-semibold text-gray-500">Component Name</Label>
                                <Input
                                  type="text"
                                  value={component.name}
                                  onChange={(e) => {
                                    const updatedRecords = [...salaryRecords];
                                    const recordIndex = updatedRecords.findIndex(r => r.id === record.id);
                                    updatedRecords[recordIndex].customComponents[compIndex].name = e.target.value;
                                    setSalaryRecords(updatedRecords);
                                  }}
                                  className="mt-1"
                                  placeholder="e.g., Performance Bonus, Night Shift Allowance, etc."
                                />
                              </div>
                              <div>
                                <Label className="text-xs font-semibold text-gray-500">Amount (₹)</Label>
                                <Input
                                  type="number"
                                  step="0.01"
                                  value={component.amount}
                                  onChange={(e) => {
                                    const updatedRecords = [...salaryRecords];
                                    const recordIndex = updatedRecords.findIndex(r => r.id === record.id);
                                    updatedRecords[recordIndex].customComponents[compIndex].amount = Number(e.target.value);
                                    setSalaryRecords(updatedRecords);
                                  }}
                                  className="mt-1"
                                  placeholder="0.00"
                                />
                              </div>
                            </div>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                const updatedRecords = [...salaryRecords];
                                const recordIndex = updatedRecords.findIndex(r => r.id === record.id);
                                updatedRecords[recordIndex].customComponents = updatedRecords[recordIndex].customComponents.filter((_: any, i: number) => i !== compIndex);
                                setSalaryRecords(updatedRecords);
                              }}
                              className="text-red-500 hover:text-red-700 hover:bg-red-50 mt-5"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Gross Monthly Summary */}
                  <div className="mt-4 p-3 bg-green-50 rounded-lg border border-green-200">
                    <div className="flex justify-between items-center">
                      <span className="font-semibold text-green-800">Gross Monthly (Full Month)</span>
                      <span className="text-xl font-bold text-green-600">
                        ₹{fmt(salaryDetails.gross)}
                      </span>
                    </div>
                    {(record.customComponents && record.customComponents.length > 0) && (
                      <div className="mt-2 pt-2 border-t border-green-200">
                        <div className="text-xs text-green-700">
                          Includes: {record.customComponents.map((c: any) => c.name).filter(n => n).join(", ")}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Statutory Deductions for this record */}
                  <div className="mt-4">
                    <div className="p-3 bg-red-50 rounded-lg border border-red-100">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <div>
                          <div className="text-xs text-gray-500">EPF (EE)</div>
                          <div className="text-sm font-bold text-red-600">₹{fmt(salaryDetails.pfEE)}</div>
                        </div>
                        <div>
                          <div className="text-xs text-gray-500">ESIC (EE)</div>
                          <div className="text-sm font-bold text-red-600">₹{fmt(salaryDetails.esicEE)}</div>
                        </div>
                        <div>
                          <div className="text-xs text-gray-500">Professional Tax</div>
                          <div className="text-sm font-bold text-red-600">₹{fmt(salaryDetails.pt)}</div>
                        </div>
                        <div>
                          <div className="text-xs text-gray-500">LWF (EE)</div>
                          <div className="text-sm font-bold text-red-600">₹{fmt(salaryDetails.lwfEE)}</div>
                        </div>
                      </div>
                    </div>

                    <div className="mt-3 p-3 bg-orange-50 rounded-lg border border-orange-100">
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-gray-500">TDS (Monthly)</span>
                        <span className="text-sm font-bold text-orange-600">₹{fmt(salaryDetails.tds)}</span>
                      </div>
                    </div>

                    <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-100">
                      <div className="flex justify-between items-center">
                        <span className="font-semibold text-blue-800">Net Pay (Take Home)</span>
                        <span className="text-xl font-bold text-blue-600">₹{fmt(salaryDetails.net)}</span>
                      </div>
                    </div>

                    <div className="mt-3 p-3 bg-purple-50 rounded-lg border border-purple-100">
                      <div className="flex justify-between items-center">
                        <span className="font-semibold text-purple-800">Monthly CTC</span>
                        <span className="text-sm font-bold text-purple-600">₹{fmt(salaryDetails.ctc)}</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}

          {/* Add Another Salary Component Button */}
          <Button
            type="button"
            variant="outline"
            onClick={addSalaryRecord}
            className="w-full border-dashed border-2 py-6 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
          >
            <Plus className="h-5 w-5 mr-2" />
            Add Another Salary Component
          </Button>

          {/* Statutory Settings Card */}
          <Card className="border-0 shadow-md">
            <CardHeader className="bg-gradient-to-r from-red-50 to-rose-50">
              <CardTitle className="text-lg flex items-center gap-2">
                <Briefcase className="h-5 w-5 text-red-600" />
                Statutory Settings
              </CardTitle>
              <CardDescription>Configure statutory deductions applicable to this employee</CardDescription>
            </CardHeader>
            <CardContent className="p-6">
              <div className="space-y-4">
                <div className="border-b pb-3">
                  <div className="flex items-center gap-2 mb-2">
                    <input
                      type="checkbox"
                      checked={pfApplicable === true}
                      onChange={(e) => setPfApplicable(e.target.checked)}
                      className="h-4 w-4 text-blue-600 rounded"
                    />
                    <label className="font-medium">EPF Applicable (EE 12% + ER 12%)</label>
                  </div>
                  {pfApplicable && (
                    <div className="flex gap-4 items-center ml-6 flex-wrap">
                      <select
                        className="border rounded p-1 text-sm"
                        value={pfBasis}
                        onChange={(e) => setPfBasis(e.target.value as "statutory" | "actual")}
                      >
                        <option value="statutory">Statutory (capped ₹15,000 basic)</option>
                        <option value="actual">Actual Basic Salary</option>
                      </select>
                    </div>
                  )}
                </div>

                <div className="border-b pb-3">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={esicApplicable === true}
                      onChange={(e) => setEsicApplicable(e.target.checked)}
                      className="h-4 w-4 text-blue-600 rounded"
                    />
                    <label className="font-medium">ESIC Applicable (gross ≤ ₹21,000)</label>
                  </div>
                </div>

                <div className="border-b pb-3">
                  <div className="flex items-center gap-2 mb-2">
                    <input
                      type="checkbox"
                      checked={ptApplicable === true}
                      onChange={(e) => setPtApplicable(e.target.checked)}
                      className="h-4 w-4 text-blue-600 rounded"
                    />
                    <label className="font-medium">Professional Tax</label>
                  </div>
                  {ptApplicable && (
                    <div className="flex gap-4 items-center ml-6 flex-wrap">
                      <select
                        className="border rounded p-1 text-sm"
                        value={ptState}
                        onChange={(e) => setPtState(e.target.value)}
                      >
                        {Object.entries(PT_STATES).map(([k, v]) => (
                          <option key={k} value={k}>{v.label}</option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>

                <div className="border-b pb-3">
                  <div className="flex items-center gap-2 mb-2">
                    <input
                      type="checkbox"
                      checked={lwfApplicable === true}
                      onChange={(e) => setLwfApplicable(e.target.checked)}
                      className="h-4 w-4 text-blue-600 rounded"
                    />
                    <label className="font-medium">Labour Welfare Fund (LWF)</label>
                  </div>
                  {lwfApplicable && (
                    <div className="flex gap-4 items-center ml-6 flex-wrap">
                      <select
                        className="border rounded p-1 text-sm"
                        value={lwfState}
                        onChange={(e) => setLwfState(e.target.value)}
                      >
                        {Object.entries(LWF_STATES).map(([k, v]) => (
                          <option key={k} value={k}>{v.label}</option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>

                <div className="border-b pb-3">
                  <div className="flex items-center gap-2 mb-2">
                    <input
                      type="checkbox"
                      checked={formData.tdsApplicable === true}
                      onChange={(e) => setFormData({ ...formData, tdsApplicable: e.target.checked })}
                      className="h-4 w-4 text-blue-600 rounded"
                    />
                    <label className="font-medium">Deduct TDS (Income Tax)</label>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* TDS Declaration Section - Only for Old Regime */}
          {formData.tdsApplicable && !isNewRegime && (
            <Card className="border-0 shadow-md">
              <CardHeader className="bg-gradient-to-r from-amber-50 to-yellow-50">
                <CardTitle className="text-lg">TDS Declaration (Form 12BB)</CardTitle>
                <CardDescription>Employee tax saving declarations for Old Regime</CardDescription>
              </CardHeader>
              <CardContent className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-gray-500">Sec 80C (PPF/ELSS/LIC/EPF) - Max ₹1,50,000</Label>
                    <Input
                      type="number"
                      value={declaration.sec80c}
                      onChange={(e) => setDeclaration({ ...declaration, sec80c: Number(e.target.value) })}
                      className="mt-1"
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">Sec 80D (Health Insurance) - Max ₹50,000</Label>
                    <Input
                      type="number"
                      value={declaration.sec80d}
                      onChange={(e) => setDeclaration({ ...declaration, sec80d: Number(e.target.value) })}
                      className="mt-1"
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">Sec 80CCD(1B) (NPS Self) - Max ₹50,000</Label>
                    <Input
                      type="number"
                      value={declaration.sec80ccd1b}
                      onChange={(e) => setDeclaration({ ...declaration, sec80ccd1b: Number(e.target.value) })}
                      className="mt-1"
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">HRA Exemption (Annual)</Label>
                    <Input
                      type="number"
                      value={declaration.hraExempt}
                      onChange={(e) => setDeclaration({ ...declaration, hraExempt: Number(e.target.value) })}
                      className="mt-1"
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">Home Loan Interest (Sec 24b) - Max ₹2,00,000</Label>
                    <Input
                      type="number"
                      value={declaration.homeLoan}
                      onChange={(e) => setDeclaration({ ...declaration, homeLoan: Number(e.target.value) })}
                      className="mt-1"
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">LTA Exemption (Annual)</Label>
                    <Input
                      type="number"
                      value={declaration.ltaExempt}
                      onChange={(e) => setDeclaration({ ...declaration, ltaExempt: Number(e.target.value) })}
                      className="mt-1"
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">Sec 80G (Donations)</Label>
                    <Input
                      type="number"
                      value={declaration.sec80g}
                      onChange={(e) => setDeclaration({ ...declaration, sec80g: Number(e.target.value) })}
                      className="mt-1"
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">Employer NPS (Sec 80CCD(2)) - Monthly</Label>
                    <Input
                      type="number"
                      value={declaration.npsEmployer}
                      onChange={(e) => setDeclaration({ ...declaration, npsEmployer: Number(e.target.value) })}
                      className="mt-1"
                      placeholder="0"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* New Regime Note */}
          {formData.tdsApplicable && isNewRegime && (
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="font-semibold text-blue-800 text-sm mb-1">New Regime Benefits</div>
              <div className="text-xs text-blue-700">
                • Standard Deduction: ₹75,000<br />
                • Rebate u/s 87A: Up to ₹60,000 for income ≤ ₹12L (effectively zero tax up to ₹12.75L)<br />
                • Only Employer NPS (80CCD(2)) is deductible<br />
                • No other deductions (80C, 80D, HRA, etc.) are allowed
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex justify-between pt-4">
            <Button type="button" variant="outline" onClick={handleBack}>
              <ChevronLeft className="h-4 w-4 mr-2" />
              Back: Personal Details
            </Button>
            <Button
              type="button"
              onClick={handleNext}
              className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700"
            >
              Next: Review & Submit
              <ChevronRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </div>
      )}

      {/* Summary Tab */}
      {activeTab === "summary" && (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold text-gray-800">Review Employee Information</h2>
          <p className="text-gray-600">Please review all details before {employee?.id ? "updating" : "creating"} the employee</p>

          {/* Personal Summary */}
          <Card className="border-0 shadow-md">
            <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50">
              <CardTitle className="text-lg">Personal Information</CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-semibold text-gray-500">Full Name</Label>
                  <p className="text-gray-900 mt-1">{formData.first_name} {formData.middle_name} {formData.last_name}</p>
                </div>
                <div>
                  <Label className="text-xs font-semibold text-gray-500">Employee ID</Label>
                  <p className="text-gray-900 mt-1 font-mono">{formData.employeeID}</p>
                </div>
                <div>
                  <Label className="text-xs font-semibold text-gray-500">Email</Label>
                  <p className="text-gray-900 mt-1">{formData.email}</p>
                </div>
                <div>
                  <Label className="text-xs font-semibold text-gray-500">Phone</Label>
                  <p className="text-gray-900 mt-1">{formData.phone || "N/A"}</p>
                </div>
                <div>
                  <Label className="text-xs font-semibold text-gray-500">Department</Label>
                  <p className="text-gray-900 mt-1">{getDepartmentName(formData.department_id)}</p>
                </div>
                <div>
                  <Label className="text-xs font-semibold text-gray-500">Role</Label>
                  <p className="text-gray-900 mt-1 capitalize">{formData.role}</p>
                </div>
                <div>
                  <Label className="text-xs font-semibold text-gray-500">Designation</Label>
                  <p className="text-gray-900 mt-1">{getDesignationName(formData.designation_id)}</p>
                </div>
                <div>
                  <Label className="text-xs font-semibold text-gray-500">Date of Joining</Label>
                  <p className="text-gray-900 mt-1">{formData.hire_date}</p>
                </div>
                <div>
                  <Label className="text-xs font-semibold text-gray-500">Date of Birth</Label>
                  <p className="text-gray-900 mt-1">{formData.date_of_birth || "N/A"}</p>
                </div>
                <div>
                  <Label className="text-xs font-semibold text-gray-500">Mode of Working</Label>
                  <p className="text-gray-900 mt-1 capitalize">{formData.modeOfWorking}</p>
                </div>
                {showReportingPerson && formData.manager_id && (
                  <div>
                    <Label className="text-xs font-semibold text-gray-500">Reporting Person</Label>
                    <p className="text-gray-900 mt-1">{getManagerName(formData.manager_id)}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Address Summary */}
          <Card className="border-0 shadow-md">
            <CardHeader className="bg-gradient-to-r from-green-50 to-emerald-50">
              <CardTitle className="text-lg">Address Information</CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-gray-700 mb-2">Current Address</h4>
                  <p className="text-gray-600">{formData.current_address1}</p>
                  {formData.current_address2 && <p className="text-gray-600">{formData.current_address2}</p>}
                  <p className="text-gray-600">{formData.current_city}, {getStateName(formData.current_state)} - {formData.current_pin}</p>
                  <p className="text-gray-600">{getCountryName(formData.country)}</p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-700 mb-2">Permanent Address</h4>
                  {formData.sameAs ? (
                    <p className="text-gray-500 italic">Same as current address</p>
                  ) : (
                    <>
                      <p className="text-gray-600">{formData.permanent_address1}</p>
                      {formData.permanent_address2 && <p className="text-gray-600">{formData.permanent_address2}</p>}
                      <p className="text-gray-600">{formData.permanent_city}, {getStateName(formData.permanent_state)} - {formData.permanent_pin}</p>
                    </>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Salary Summary - Show all records */}
          <Card className="border-0 shadow-md">
            <CardHeader className="bg-gradient-to-r from-purple-50 to-pink-50">
              <CardTitle className="text-lg">Salary Details</CardTitle>
              <CardDescription>Tax Regime: {formData.taxRegime === 'new' ? 'New Regime' : 'Old Regime'}</CardDescription>
            </CardHeader>
            <CardContent className="p-6">
              {salaryRecords.map((record, idx) => {
                const details = calculateSalaryDetailsForRecord(record);
                return (
                  <div key={record.id} className={`${idx > 0 ? 'mt-6 pt-6 border-t border-gray-200' : ''}`}>
                    <div className="mb-3 flex justify-between items-center">
                      <h4 className="font-semibold text-gray-700">Salary Component {idx + 1}</h4>
                      <div className="flex gap-3 text-xs">
                        <span className="text-gray-500">Effective: {record.effectiveDate}</span>
                        <span className={`px-2 py-0.5 rounded-full text-xs ${record.status === 'active' ? 'bg-green-100 text-green-700' : record.status === 'pending' ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-700'}`}>
                          {record.status}
                        </span>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="bg-green-50 p-4 rounded-lg">
                        <h4 className="font-semibold text-green-800 mb-3">Earnings</h4>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between"><span>Basic Salary:</span><span>₹{fmt(record.basicSalary)}</span></div>
                          <div className="flex justify-between"><span>HRA:</span><span>₹{fmt(record.hra)}</span></div>
                          <div className="flex justify-between"><span>Conveyance:</span><span>₹{fmt(record.conveyance)}</span></div>
                          <div className="flex justify-between"><span>Medical Allowance:</span><span>₹{fmt(record.medicalallowance)}</span></div>
                          <div className="flex justify-between"><span>Special Allowance:</span><span>₹{fmt(record.allowance)}</span></div>
                          <div className="flex justify-between"><span>LTA:</span><span>₹{fmt(record.lta)}</span></div>
                          {record.others > 0 && <div className="flex justify-between"><span>Others:</span><span>₹{fmt(record.others)}</span></div>}
                          {record.bonusAmount > 0 && <div className="flex justify-between"><span>Bonus:</span><span>₹{fmt(record.bonusAmount)}</span></div>}
                          <div className="flex justify-between pt-2 border-t mt-2 font-semibold"><span>Gross Monthly:</span><span>₹{fmt(details.gross)}</span></div>
                        </div>
                      </div>
                      <div className="bg-red-50 p-4 rounded-lg">
                        <h4 className="font-semibold text-red-800 mb-3">Deductions</h4>
                        <div className="space-y-2 text-sm">
                          {pfApplicable && <div className="flex justify-between"><span>EPF (EE):</span><span>₹{fmt(details.pfEE)}</span></div>}
                          {esicApplicable && details.esicEE > 0 && <div className="flex justify-between"><span>ESIC (EE):</span><span>₹{fmt(details.esicEE)}</span></div>}
                          {ptApplicable && <div className="flex justify-between"><span>Professional Tax:</span><span>₹{fmt(details.pt)}</span></div>}
                          {lwfApplicable && <div className="flex justify-between"><span>LWF (EE):</span><span>₹{fmt(details.lwfEE)}</span></div>}
                          {formData.tdsApplicable && <div className="flex justify-between"><span>TDS:</span><span>₹{fmt(details.tds)}</span></div>}
                          <div className="flex justify-between pt-2 border-t mt-2 font-semibold"><span>Total Deductions:</span><span>₹{fmt(details.totDed)}</span></div>
                        </div>
                      </div>
                    </div>
                    <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                      <div className="flex justify-between items-center">
                        <span className="font-semibold text-blue-800">Net Pay (Take Home)</span>
                        <span className="text-2xl font-bold text-blue-600">₹{fmt(details.net)}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>

          {/* Navigation Buttons */}
          <div className="flex justify-between pt-4">
            <Button type="button" variant="outline" onClick={handleBack}>
              <ChevronLeft className="h-4 w-4 mr-2" />
              Back: Salary Details
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  {employee?.id ? "Updating..." : "Creating..."}
                </>
              ) : (
                <>
                  <UserPlus className="h-4 w-4 mr-2" />
                  {employee?.id ? "Update Employee" : "Create Employee"}
                </>
              )}
            </Button>
          </div>
        </div>
      )}

      {/* Add Department Modal */}
      <Dialog open={addDepartmentModal} onOpenChange={setAddDepartmentModal}>
        <DialogContent className="max-w-sm bg-white">
          <DialogHeader>
            <DialogTitle>Add New Department</DialogTitle>
          </DialogHeader>
          <div>
            <Label htmlFor="departmentName">Department Name</Label>
            <Input
              id="departmentName"
              value={departmentName}
              onChange={(e) => setDepartmentName(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button onClick={() => setAddDepartmentModal(false)} variant="outline">
              Close
            </Button>
            <Button onClick={handleAddDepartment}>
              Add Department
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Designation Modal */}
      <Dialog open={addDesignationModal} onOpenChange={setAddDesignationModal}>
        <DialogContent className="max-w-sm bg-white">
          <DialogHeader>
            <DialogTitle>Add New Designation</DialogTitle>
          </DialogHeader>
          <div>
            <Label htmlFor="designationName">Designation Name</Label>
            <Input
              id="designationName"
              value={designationName}
              onChange={(e) => setDesignationName(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button onClick={() => setAddDesignationModal(false)} variant="outline">
              Close
            </Button>
            <Button onClick={handleAddDesignation}>
              Add Designation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </form>
  );
}

function EmployeeDetails({
  employee,
  activeTab: externalActiveTab,
  onTabChange
}: {
  employee: UserType;
  activeTab?: "personal" | "employment" | "documents";
  onTabChange?: (tab: "personal" | "employment" | "documents") => void;
}) {
  const [internalActiveTab, setInternalActiveTab] = useState<"personal" | "employment" | "documents">(
    externalActiveTab || "personal"
  );

  const [departmentName, setDepartmentName] = useState("");
  const [designationName, setDesignationName] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  // Use external tab if provided, otherwise use internal
  const currentActiveTab = externalActiveTab || internalActiveTab;

  const setActiveTab = (tab: "personal" | "employment" | "documents") => {
    if (onTabChange) {
      onTabChange(tab);
    } else {
      setInternalActiveTab(tab);
    }
  };

  // Offer Letter states
  const [showOfferModal, setShowOfferModal] = useState(false);
  const [offerConfig, setOfferConfig] = useState({
    validTill: "",
    status: "Pending",
    date: new Date().toISOString().split('T')[0],
    terms_and_conditions: "",
  });
  const [isOfferLoading, setIsOfferLoading] = useState(false);

  // Joining Letter states
  const [showJoiningModal, setShowJoiningModal] = useState(false);
  const [joiningConfig, setJoiningConfig] = useState({
    probationPeriod: "",
    terms_and_conditions: "",
  });
  const [isJoiningLoading, setIsJoiningLoading] = useState(false);

  // Salary Slip states
  const [showSalaryModal, setShowSalaryModal] = useState(false);
  const [showSalaryCheckModal, setShowSalaryCheckModal] = useState(false);
  const [salaryConfig, setSalaryConfig] = useState({
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
  });
  const [isSalaryLoading, setIsSalaryLoading] = useState(false);
  const [isSalaryEmailLoading, setIsSalaryEmailLoading] = useState(false);
  const [isSalaryConfigured, setIsSalaryConfigured] = useState(false);

  // Email modal states
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailType, setEmailType] = useState<"offer" | "joining" | "salary">("offer");
  const [emailRecipient, setEmailRecipient] = useState(employee.email || "");
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  // Add these states inside EmployeeDetails component after existing states
  const [termsList, setTermsList] = useState<any[]>([]);
  const [showAddTermsModal, setShowAddTermsModal] = useState(false);
  const [newTermsData, setNewTermsData] = useState({
    title: '',
    content: ''
  });
  const [isSavingTerms, setIsSavingTerms] = useState(false);
  const [selectedTermsId, setSelectedTermsId] = useState<string>("");

  // Add these states with existing ones
  const [joiningTermsList, setJoiningTermsList] = useState<any[]>([]);
  const [selectedJoiningTermsId, setSelectedJoiningTermsId] = useState<string>("");
  const [showAddJoiningTermsModal, setShowAddJoiningTermsModal] = useState(false);
  const [newJoiningTermsData, setNewJoiningTermsData] = useState({
    title: '',
    content: ''
  });
  const [isSavingJoiningTerms, setIsSavingJoiningTerms] = useState(false);

  const { toast } = useToast();
  const departmentList = useSelector((state: any) => state.employee.departmentList) || [];
  const designationList = useSelector((state: any) => state.employee.designationList) || [];

  useEffect(() => {
    dispatch(getDepartmentList());
  }, []);

  // Add this useEffect inside EmployeeDetails component
  useEffect(() => {
    fetchTermsList();
  }, []);

  // Add these functions inside EmployeeDetails component
  const fetchTermsList = async () => {
    try {
      const response = await termsConditionsApi.getTermsConditionsList();
      let data = [];
      if (response && response.data && Array.isArray(response.data)) {
        data = response.data;
      } else if (response && Array.isArray(response)) {
        data = response;
      } else {
        data = [];
      }
      // Filter only offer letter type
      const offerTerms = data.filter((term: any) => term.letter_type?.toLowerCase() === 'offer');
      setTermsList(offerTerms);
    } catch (error) {
      console.error("Failed to fetch terms:", error);
    }
  };

  const handleSaveNewTerms = async () => {
    if (!newTermsData.title || !newTermsData.content) {
      toast({ // title: "Error", 
        description: "Please fill all required fields", variant: "destructive"
      });
      return;
    }

    setIsSavingTerms(true);
    try {
      const response = await termsConditionsApi.createTermsConditions({
        title: newTermsData.title,
        letter_type: 'offer',
        content: newTermsData.content
      });

      if (response.success !== false) {
        toast({ title: "Success", description: "Terms & Conditions added successfully" });
        await fetchTermsList();
        setShowAddTermsModal(false);
        setNewTermsData({ title: '', content: '' });

        const newTermsId = response.data?.id || response.id;
        if (newTermsId) {
          setSelectedTermsId(String(newTermsId));
          const selectedTerm = termsList.find(t => String(t.id) === String(newTermsId)) || (response.data || response);
          if (selectedTerm) {
            setOfferConfig(prev => ({ ...prev, terms_and_conditions: selectedTerm.content }));
          }
        }
      }
    } catch (error: any) {
      toast({ // title: "Error", 
        description: error.message || error.detail || "Failed to save terms", variant: "destructive"
      });
    } finally {
      setIsSavingTerms(false);
    }
  };

  const handleTermsSelect = (value: string) => {
    if (value === "add_new") {
      setShowAddTermsModal(true);
      setSelectedTermsId("");
    } else {
      setSelectedTermsId(value);  // This stores the ID
      const selectedTerm = termsList.find((term: any) => String(term.id) === value);
      if (selectedTerm) {
        // Store the content for preview only, not for API
        setOfferConfig(prev => ({ ...prev, terms_and_conditions: selectedTerm.content }));
      }
    }
  };

  // Add this useEffect to fetch joining terms
  useEffect(() => {
    fetchJoiningTermsList();
  }, []);

  // Function to fetch joining letter terms
  const fetchJoiningTermsList = async () => {
    try {
      const response = await termsConditionsApi.getTermsConditionsList();
      let data = [];
      if (response && response.data && Array.isArray(response.data)) {
        data = response.data;
      } else if (response && Array.isArray(response)) {
        data = response;
      } else {
        data = [];
      }
      // Filter only joining letter type
      const joiningTerms = data.filter((term: any) => term.letter_type?.toLowerCase() === 'joining');
      setJoiningTermsList(joiningTerms);
    } catch (error) {
      console.error("Failed to fetch joining terms:", error);
    }
  };

  // Function to save new joining terms
  const handleSaveNewJoiningTerms = async () => {
    if (!newJoiningTermsData.title || !newJoiningTermsData.content) {
      toast({ // title: "Error", 
        description: "Please fill all required fields", variant: "destructive"
      });
      return;
    }

    setIsSavingJoiningTerms(true);
    try {
      const response = await termsConditionsApi.createTermsConditions({
        title: newJoiningTermsData.title,
        letter_type: 'joining',
        content: newJoiningTermsData.content
      });

      if (response.success !== false) {
        toast({ title: "Success", description: "Terms & Conditions added successfully" });
        await fetchJoiningTermsList();
        setShowAddJoiningTermsModal(false);
        setNewJoiningTermsData({ title: '', content: '' });

        const newTermsId = response.data?.id || response.id;
        if (newTermsId) {
          setSelectedJoiningTermsId(String(newTermsId));
          const selectedTerm = joiningTermsList.find(t => String(t.id) === String(newTermsId)) || (response.data || response);
          if (selectedTerm) {
            setJoiningConfig(prev => ({ ...prev, terms_and_conditions: selectedTerm.content }));
          }
        }
      }
    } catch (error: any) {
      toast({ // title: "Error",
        description: error.message || error.detail || "Failed to save terms", variant: "destructive"
      });
    } finally {
      setIsSavingJoiningTerms(false);
    }
  };

  // Function to handle joining terms selection
  const handleJoiningTermsSelect = (value: string) => {
    if (value === "add_new") {
      setShowAddJoiningTermsModal(true);
      setSelectedJoiningTermsId("");
    } else {
      setSelectedJoiningTermsId(value);
      const selectedTerm = joiningTermsList.find((term: any) => String(term.id) === value);
      if (selectedTerm) {
        setJoiningConfig(prev => ({ ...prev, terms_and_conditions: selectedTerm.content }));
      }
    }
  };

  useEffect(() => {
    if (employee?.department_id && departmentList.length > 0) {
      const dept = departmentList.find((d: { id: string }) => d.id === employee.department_id);
      setDepartmentName(dept?.name || "-");
    }
  }, [employee?.department_id, departmentList]);

  useEffect(() => {
    if (employee?.designation_id && designationList.length > 0) {
      const designation = designationList.find((d: { id: string }) => d.id === employee.designation_id);
      setDesignationName(designation?.name || "-");
    }
  }, [employee?.designation_id, designationList]);

  const getMonthName = (month: number): string => {
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    return monthNames[month - 1];
  };

  const openOfferConfig = () => setShowOfferModal(true);
  const openJoiningConfig = () => setShowJoiningModal(true);

  const openSalaryConfig = () => {
    setShowSalaryCheckModal(true);
  };

  const saveOfferConfig = () => {
    if (!offerConfig.validTill) {
      toast({ // title: "Error", 
        description: "Please select Offer Valid Till date", variant: "destructive"
      });
      return;
    }
    // if (!selectedTermsId) {
    //   toast({ title: "Error", description: "Please select Terms & Conditions", variant: "destructive" });
    //   return;
    // }
    setShowOfferModal(false);
    toast({ title: "Success", description: "Offer letter configuration saved" });
  };

  const saveJoiningConfig = () => {
    // if (!joiningConfig.probationPeriod) {
    //   toast({ // title: "Error", 
    //     description: "Please select Probation Period end date", variant: "destructive" });
    //   return;
    // }
    setShowJoiningModal(false);
    toast({ title: "Success", description: "Joining letter configuration saved" });
  };

  const saveSalaryConfig = () => {
    if (!salaryConfig.month || !salaryConfig.year) {
      toast({ // title: "Error",
        description: "Please select month and year", variant: "destructive"
      });
      return;
    }
    setShowSalaryModal(false);
    setIsSalaryConfigured(true);
    toast({ title: "Success", description: "Salary slip configuration saved" });
  };

  const handleDownloadOfferLetter = async () => {
    if (!offerConfig.validTill) {
      openOfferConfig();
      return;
    }
    // if (!selectedTermsId) {
    //   toast({ // title: "Error",
    //     description: "Please select Terms & Conditions first", variant: "destructive" });
    //   return;
    // }
    setIsOfferLoading(true);
    try {
      // Send the tc_id, not the description
      await downloadOfferLetter(
        employee.id,
        offerConfig.validTill,
        offerConfig.date,
        offerConfig.status,
        selectedTermsId  // This sends the ID, not the description
      );
      toast({ title: "Success", description: "Offer letter downloaded successfully" });
    } catch (error: any) {
      toast({ // title: "Error",
        description: error.message || "Failed to download", variant: "destructive"
      });
    } finally {
      setIsOfferLoading(false);
    }
  };

  // const handleDownloadJoiningLetter = async () => {
  //   if (!joiningConfig.probationPeriod) {
  //     openJoiningConfig();
  //     return;
  //   }
  //   setIsJoiningLoading(true);
  //   try {
  //     await downloadJoiningLetter(employee.id, joiningConfig.probationPeriod, selectedTermsId);
  //     toast({ title: "Success", description: "Joining letter downloaded successfully" });
  //   } catch (error: any) {
  //     toast({ title: "Error", description: error.message || "Failed to download", variant: "destructive" });
  //   } finally {
  //     setIsJoiningLoading(false);
  //   }
  // };

  const handleDownloadJoiningLetter = async () => {
    // if (!joiningConfig.probationPeriod) {
    //   openJoiningConfig();
    //   return;
    // }
    // if (!selectedJoiningTermsId) {
    //   toast({ // title: "Error",
    //     description: "Please select Terms & Conditions first", variant: "destructive" });
    //   return;
    // }
    setIsJoiningLoading(true);
    try {
      await downloadJoiningLetter(employee.id, joiningConfig.probationPeriod, selectedJoiningTermsId);
      toast({ title: "Success", description: "Joining letter downloaded successfully" });
    } catch (error: any) {
      toast({ // title: "Error", 
        description: error.message || error.detail || "Failed to download", variant: "destructive"
      });
    } finally {
      setIsJoiningLoading(false);
    }
  };

  const handleDownloadSalarySlip = async () => {
    if (!isSalaryConfigured || !salaryConfig.month || !salaryConfig.year) {
      toast({ title: "Configuration Required", description: "Please configure salary slip first", variant: "destructive" });
      openSalaryConfig();
      return;
    }
    setIsSalaryLoading(true);
    try {
      await downloadSalarySlip(employee.id, salaryConfig.month, salaryConfig.year);
      toast({ title: "Success", description: `Salary slip for ${getMonthName(salaryConfig.month)} ${salaryConfig.year} downloaded` });
    } catch (error: any) {
      toast({ title: "Error", description: error.message || "Failed to download", variant: "destructive" });
    } finally {
      setIsSalaryLoading(false);
    }
  };

  const handleSendOfferEmail = async () => {
    if (!offerConfig.validTill) {
      toast({ title: "Configuration Required", description: "Please configure offer letter first", variant: "destructive" });
      openOfferConfig();
      return;
    }
    // if (!selectedTermsId) {
    //   toast({ // title: "Error",
    //     description: "Please select Terms & Conditions first", variant: "destructive" });
    //   return;
    // }
    if (!emailRecipient) {
      toast({ // title: "Error",
        description: "Please enter recipient email", variant: "destructive"
      });
      return;
    }
    setIsSendingEmail(true);
    try {
      // Send the tc_id, not the description
      await sendOfferLetterEmail(
        employee.id,
        offerConfig.validTill,
        offerConfig.date,
        offerConfig.status,
        selectedTermsId  // This sends the ID, not the description
      );
      toast({ title: "Success", description: `Offer letter sent to ${emailRecipient}` });
      setShowEmailModal(false);
    } catch (error: any) {
      toast({ // title: "Error", 
        description: error.message || error.detail || "Failed to send", variant: "destructive"
      });
    } finally {
      setIsSendingEmail(false);
    }
  };

  // const handleSendJoiningEmail = async () => {
  //   if (!joiningConfig.probationPeriod) {
  //     toast({ title: "Configuration Required", description: "Please configure joining letter first", variant: "destructive" });
  //     openJoiningConfig();
  //     return;
  //   }
  //   if (!emailRecipient) {
  //     toast({ title: "Error", description: "Please enter recipient email", variant: "destructive" });
  //     return;
  //   }
  //   setIsSendingEmail(true);
  //   try {
  //     await sendjoiningLetterEmail(employee.id, joiningConfig.probationPeriod, joiningConfig.terms_and_conditions);
  //     toast({ title: "Success", description: `Joining letter sent to ${emailRecipient}` });
  //     setShowEmailModal(false);
  //   } catch (error: any) {
  //     toast({ title: "Error", description: error.message || "Failed to send", variant: "destructive" });
  //   } finally {
  //     setIsSendingEmail(false);
  //   }
  // };

  const handleSendJoiningEmail = async () => {
    // if (!joiningConfig.probationPeriod) {
    //   toast({ title: "Configuration Required", description: "Please configure joining letter first", variant: "destructive" });
    //   openJoiningConfig();
    //   return;
    // }
    // if (!selectedJoiningTermsId) {
    //   toast({ // title: "Error", 
    //     description: "Please select Terms & Conditions first", variant: "destructive" });
    //   return;
    // }
    if (!emailRecipient) {
      toast({ // title: "Error",
        description: "Please enter recipient email", variant: "destructive"
      });
      return;
    }
    setIsSendingEmail(true);
    try {
      await sendjoiningLetterEmail(employee.id, joiningConfig.probationPeriod, selectedJoiningTermsId);
      toast({ title: "Success", description: `Joining letter sent to ${emailRecipient}` });
      setShowEmailModal(false);
    } catch (error: any) {
      toast({ // title: "Error",
        description: error.message || error.detail || "Failed to send", variant: "destructive"
      });
    } finally {
      setIsSendingEmail(false);
    }
  };

  const handleSendSalaryEmail = async () => {
    if (!isSalaryConfigured || !salaryConfig.month || !salaryConfig.year) {
      toast({ title: "Configuration Required", description: "Please configure salary slip first", variant: "destructive" });
      openSalaryConfig();
      return;
    }
    if (!emailRecipient) {
      toast({ // title: "Error",
        description: "Please enter recipient email", variant: "destructive"
      });
      return;
    }
    setIsSalaryEmailLoading(true);
    try {
      await sendSalarySlipEmail(employee.id, salaryConfig.month, salaryConfig.year);
      toast({ title: "Success", description: `Salary slip for ${getMonthName(salaryConfig.month)} ${salaryConfig.year} sent to ${emailRecipient}` });
      setShowEmailModal(false);
    } catch (error: any) {
      toast({ // title: "Error",
        description: error.message || error.detail || "Failed to send", variant: "destructive"
      });
    } finally {
      setIsSalaryEmailLoading(false);
    }
  };

  const openEmailModal = (type: "offer" | "joining" | "salary") => {
    if (type === "offer" && !offerConfig.validTill) {
      toast({ title: "Configuration Required", description: "Please configure offer letter first", variant: "destructive" });
      openOfferConfig();
      return;
    }
    if (type === "joining" && !joiningConfig.probationPeriod) {
      toast({ title: "Configuration Required", description: "Please configure joining letter first", variant: "destructive" });
      openJoiningConfig();
      return;
    }
    if (type === "salary" && (!isSalaryConfigured || !salaryConfig.month || !salaryConfig.year)) {
      toast({ title: "Configuration Required", description: "Please configure salary slip first", variant: "destructive" });
      openSalaryConfig();
      return;
    }
    setEmailType(type);
    setEmailRecipient(employee.email || "");
    setShowEmailModal(true);
  };

  const monthOptions = Array.from({ length: 12 }, (_, i) => ({ value: i + 1, label: getMonthName(i + 1) }));
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i);

  return (
    <>
      <Tabs value={currentActiveTab} onValueChange={(value) => setActiveTab(value as any)} className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="personal">Personal Info</TabsTrigger>
          <TabsTrigger value="employment">Employment</TabsTrigger>
          <TabsTrigger value="documents">Documents</TabsTrigger>
        </TabsList>

        <TabsContent value="personal" className="space-y-4 mt-4">
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Full Name</Label><div className="p-2 bg-gray-50 rounded text-sm">{employee.first_name} {employee.last_name}</div></div>
            <div><Label>Email</Label><div className="p-2 bg-gray-50 rounded text-sm">{employee.email}</div></div>
            <div><Label>Phone</Label><div className="p-2 bg-gray-50 rounded text-sm">{employee.phone || "N/A"}</div></div>
            <div><Label>Employee ID</Label><div className="p-2 bg-gray-50 rounded text-sm">{employee.employeeID}</div></div>
            <div><Label>Date of Birth</Label><div className="p-2 bg-gray-50 rounded text-sm">{employee.date_of_birth || "N/A"}</div></div>
            <div><Label>Emergency Contact</Label><div className="p-2 bg-gray-50 rounded text-sm">{employee.emergency_contacts || "N/A"}</div></div>
          </div>
        </TabsContent>

        <TabsContent value="employment" className="space-y-4 mt-4">
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Department</Label><div className="p-2 bg-gray-50 rounded text-sm">{departmentName}</div></div>
            <div><Label>Designation</Label><div className="p-2 bg-gray-50 rounded text-sm">{employee.designation}</div></div>
            <div><Label>Role</Label><div className="p-2 bg-gray-50 rounded text-sm capitalize">{employee.role}</div></div>
            <div><Label>Date of Joining</Label><div className="p-2 bg-gray-50 rounded text-sm">{new Date(employee.hire_date).toLocaleDateString()}</div></div>
            <div><Label>Status</Label><div className="p-2 bg-gray-50 rounded"><Badge variant={employee.is_active ? "default" : "secondary"}>{employee.is_active ? "Active" : "Inactive"}</Badge></div></div>
            <div><Label>Tax Regime</Label><div className="p-2 bg-gray-50 rounded text-sm capitalize">{employee.taxRegime || "New"} Regime</div></div>
          </div>
        </TabsContent>

        <TabsContent value="documents" className="space-y-4 mt-4">
          {/* Offer Letter Card */}
          <div className="border rounded-lg overflow-hidden">
            <div className={`px-4 py-3 ${offerConfig.validTill ? 'bg-blue-100' : 'bg-blue-50'} border-b flex justify-between items-center`}>
              <div>
                <h3 className="font-semibold text-blue-800 flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Offer Letter
                </h3>
                <p className="text-xs text-blue-600">Generate and send offer letter</p>
              </div>
              {offerConfig.validTill && <Badge className="bg-green-500 text-white text-xs">Configured</Badge>}
            </div>
            <div className="p-4">
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={openOfferConfig} className="flex-1 text-sm">
                  <Edit className="h-3 w-3 mr-1" /> {offerConfig.validTill ? "Edit" : "Configure"}
                </Button>
                <Button size="sm" variant="outline" onClick={handleDownloadOfferLetter} disabled={!offerConfig.validTill} className="flex-1 text-sm">
                  {isOfferLoading ? <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-current"></div> : <Download className="h-3 w-3 mr-1" />}
                  Download
                </Button>
                <Button size="sm" variant="outline" onClick={() => openEmailModal("offer")} disabled={!offerConfig.validTill} className="flex-1 text-sm">
                  <Mail className="h-3 w-3 mr-1" /> Send
                </Button>
              </div>
              {offerConfig.validTill && (
                <p className="text-xs text-green-600 mt-2">Valid till: {new Date(offerConfig.validTill).toLocaleDateString()}</p>
              )}
            </div>
          </div>

          {/* Joining Letter Card */}
          <div className="border rounded-lg overflow-hidden">
            <div className={`px-4 py-3 ${joiningConfig.probationPeriod ? 'bg-green-100' : 'bg-green-50'} border-b flex justify-between items-center`}>
              <div>
                <h3 className="font-semibold text-green-800 flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Joining Letter
                </h3>
                <p className="text-xs text-green-600">Generate and send joining letter</p>
              </div>
              {joiningConfig.probationPeriod && <Badge className="bg-green-500 text-white text-xs">Configured</Badge>}
            </div>
            <div className="p-4">
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={openJoiningConfig} className="flex-1 text-sm">
                  <Edit className="h-3 w-3 mr-1" /> {joiningConfig.probationPeriod ? "Edit" : "Configure"}
                </Button>
                <Button size="sm" variant="outline" onClick={handleDownloadJoiningLetter} className="flex-1 text-sm">
                  {isJoiningLoading ? <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-current"></div> : <Download className="h-3 w-3 mr-1" />}
                  Download
                </Button>
                <Button size="sm" variant="outline" onClick={() => openEmailModal("joining")} className="flex-1 text-sm">
                  <Mail className="h-3 w-3 mr-1" /> Send
                </Button>
              </div>
              {joiningConfig.probationPeriod && (
                <p className="text-xs text-green-600 mt-2">Probation ends: {new Date(joiningConfig.probationPeriod).toLocaleDateString()}</p>
              )}
            </div>
          </div>


          {/* Salary Slip Card */}
          <div className="border rounded-lg overflow-hidden">
            <div className={`px-4 py-3 ${isSalaryConfigured && salaryConfig.month && salaryConfig.year ? 'bg-purple-100' : 'bg-purple-50'} border-b flex justify-between items-center`}>
              <div>
                <h3 className="font-semibold text-purple-800 flex items-center gap-2">
                  <DollarSign className="h-4 w-4" />
                  Salary Slip
                </h3>
                <p className="text-xs text-purple-600">Generate and send salary slip</p>
              </div>
              {isSalaryConfigured && salaryConfig.month && salaryConfig.year && <Badge className="bg-green-500 text-white text-xs">Configured</Badge>}
            </div>
            <div className="p-4">
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={openSalaryConfig} className="flex-1 text-sm">
                  <Edit className="h-3 w-3 mr-1" /> {isSalaryConfigured && salaryConfig.month && salaryConfig.year ? "Edit" : "Configure"}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleDownloadSalarySlip}
                  disabled={!isSalaryConfigured || !salaryConfig.month || !salaryConfig.year}
                  className="flex-1 text-sm"
                >
                  {isSalaryLoading ? <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-current"></div> : <Download className="h-3 w-3 mr-1" />}
                  Download
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => openEmailModal("salary")}
                  disabled={!isSalaryConfigured || !salaryConfig.month || !salaryConfig.year}
                  className="flex-1 text-sm"
                >
                  {isSalaryEmailLoading ? <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-current"></div> : <Mail className="h-3 w-3 mr-1" />}
                  Send
                </Button>
              </div>
              {isSalaryConfigured && salaryConfig.month && salaryConfig.year && (
                <p className="text-xs text-purple-600 mt-2">{getMonthName(salaryConfig.month)} {salaryConfig.year}</p>
              )}
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* All Modals remain the same as before */}
      {/* Offer Letter Modal */}
      {/* <Dialog open={showOfferModal} onOpenChange={setShowOfferModal}>
        <DialogContent className="max-w-sm bg-white">
          <DialogHeader>
            <DialogTitle className="text-base">Configure Offer Letter</DialogTitle>
            <DialogDescription className="text-xs">Set offer letter details</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs">Offer Date</Label>
              <Input type="date" value={offerConfig.date} onChange={(e) => setOfferConfig({ ...offerConfig, date: e.target.value })} className="mt-1 h-8 text-sm" />
            </div>
            <div>
              <Label className="text-xs">Valid Till <span className="text-red-500">*</span></Label>
              <Input type="date" value={offerConfig.validTill} onChange={(e) => setOfferConfig({ ...offerConfig, validTill: e.target.value })} className="mt-1 h-8 text-sm" />
            </div>
            <div>
              <Label className="text-xs">Status</Label>
              <Select value={offerConfig.status} onValueChange={(value) => setOfferConfig({ ...offerConfig, status: value })}>
                <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Pending">Pending</SelectItem>
                  <SelectItem value="Accepted">Accepted</SelectItem>
                  <SelectItem value="Rejected">Rejected</SelectItem>
                  <SelectItem value="Expired">Expired</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Terms & Conditions</Label>
              <Input type="text" value={offerConfig.terms_and_conditions} onChange={(e) => setOfferConfig({ ...offerConfig, terms_and_conditions: e.target.value })} className="mt-1 h-8 text-sm" placeholder="Enter terms and conditions..." />
            </div>
          </div>
          <DialogFooter><Button size="sm" onClick={saveOfferConfig} className="bg-blue-600">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog> */}
      {/* Offer Letter Modal - Updated with Terms & Conditions Dropdown */}
      <Dialog open={showOfferModal} onOpenChange={setShowOfferModal}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader>
            <DialogTitle className="text-base">Configure Offer Letter</DialogTitle>
            <DialogDescription className="text-xs">Set offer letter details</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs">Offer Date</Label>
              <Input
                type="date"
                value={offerConfig.date}
                onChange={(e) => setOfferConfig({ ...offerConfig, date: e.target.value })}
                className="mt-1 h-8 text-sm"
              />
            </div>

            <div>
              <Label className="text-xs">Valid Till <span className="text-red-500">*</span></Label>
              <Input
                type="date"
                value={offerConfig.validTill}
                onChange={(e) => setOfferConfig({ ...offerConfig, validTill: e.target.value })}
                className="mt-1 h-8 text-sm"
              />
            </div>

            <div>
              <Label className="text-xs">Status</Label>
              <Select value={offerConfig.status} onValueChange={(value) => setOfferConfig({ ...offerConfig, status: value })}>
                <SelectTrigger className="h-8 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Pending">Pending</SelectItem>
                  <SelectItem value="Accepted">Accepted</SelectItem>
                  <SelectItem value="Rejected">Rejected</SelectItem>
                  <SelectItem value="Expired">Expired</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs">Terms & Conditions <span className="text-red-500">*</span></Label>
              <Select value={selectedTermsId} onValueChange={handleTermsSelect}>
                <SelectTrigger className="mt-1 h-8 text-sm">
                  <SelectValue placeholder="Select Terms & Conditions" />
                </SelectTrigger>
                <SelectContent className="bg-white max-h-60">
                  {termsList.map((term: any) => (
                    <SelectItem key={term.id} value={String(term.id)}>
                      <div className="flex flex-col">
                        <span className="text-sm font-medium">{term.title}</span>
                        <span className="text-xs text-gray-500 truncate max-w-[200px]">
                          {term.content.substring(0, 50)}...
                        </span>
                      </div>
                    </SelectItem>
                  ))}
                  <div className="border-t my-1"></div>
                  <SelectItem value="add_new" className="text-blue-600 font-medium">
                    <div className="flex items-center gap-2">
                      <Plus className="h-3 w-3" />
                      <span>Add New Terms & Conditions</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">Select terms & conditions for the offer letter</p>
            </div>

            {/* Preview selected terms */}
            {selectedTermsId && selectedTermsId !== "add_new" && offerConfig.terms_and_conditions && (
              <div className="bg-gray-50 rounded-lg p-3 max-h-32 overflow-y-auto">
                <p className="text-xs text-gray-600 whitespace-pre-wrap">
                  {offerConfig.terms_and_conditions}
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button size="sm" variant="outline" onClick={() => setShowOfferModal(false)}>Cancel</Button>
            <Button size="sm" onClick={saveOfferConfig} className="bg-blue-600">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add New Terms & Conditions Modal */}
      <Dialog open={showAddTermsModal} onOpenChange={setShowAddTermsModal}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader>
            <DialogTitle className="text-base">Add New Terms & Conditions</DialogTitle>
            <DialogDescription className="text-xs">Create new terms for offer letters</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs">Title <span className="text-red-500">*</span></Label>
              <Input
                type="text"
                value={newTermsData.title}
                onChange={(e) => setNewTermsData({ ...newTermsData, title: e.target.value })}
                className="mt-1 h-8 text-sm"
                placeholder="e.g., Standard Offer Terms, 2024 Terms"
              />
            </div>
            <div>
              <Label className="text-xs">Content <span className="text-red-500">*</span></Label>
              <textarea
                value={newTermsData.content}
                onChange={(e) => setNewTermsData({ ...newTermsData, content: e.target.value })}
                className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                rows={6}
                placeholder="Enter the terms and conditions content..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button size="sm" variant="outline" onClick={() => setShowAddTermsModal(false)}>Cancel</Button>
            <Button size="sm" onClick={handleSaveNewTerms} disabled={isSavingTerms} className="bg-blue-600">
              {isSavingTerms ? "Saving..." : "Save Terms"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Joining Letter Modal */}
      {/* <Dialog open={showJoiningModal} onOpenChange={setShowJoiningModal}>
        <DialogContent className="max-w-sm bg-white">
          <DialogHeader>
            <DialogTitle className="text-base">Configure Joining Letter</DialogTitle>
            <DialogDescription className="text-xs">Set probation period end date</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs">Probation End Date <span className="text-red-500">*</span></Label>
              <Input type="date" value={joiningConfig.probationPeriod} onChange={(e) => setJoiningConfig({ probationPeriod: e.target.value })} className="mt-1 h-8 text-sm" min={employee.hire_date?.split('T')[0]} />
            </div>
            <div>
              <Label className="text-xs">Terms & Conditions</Label>
              <Input type="text" value={joiningConfig.terms_and_conditions} onChange={(e) => setJoiningConfig({ ...joiningConfig, terms_and_conditions: e.target.value })} className="mt-1 h-8 text-sm" placeholder="Enter terms and conditions..." />
            </div>
          </div>
          <DialogFooter><Button size="sm" onClick={saveJoiningConfig} className="bg-green-600">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog> */}

      {/* Joining Letter Modal - Updated with Terms & Conditions Dropdown */}
      <Dialog open={showJoiningModal} onOpenChange={setShowJoiningModal}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader>
            <DialogTitle className="text-base">Configure Joining Letter</DialogTitle>
            <DialogDescription className="text-xs">Set probation period and terms & conditions</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs">Probation End Date <span className="text-red-500">*</span></Label>
              <Input
                type="date"
                value={joiningConfig.probationPeriod}
                onChange={(e) => setJoiningConfig({ ...joiningConfig, probationPeriod: e.target.value })}
                className="mt-1 h-8 text-sm"
                min={employee.hire_date?.split('T')[0]}
              />
            </div>

            <div>
              <Label className="text-xs">Terms & Conditions <span className="text-red-500">*</span></Label>
              <Select value={selectedJoiningTermsId} onValueChange={handleJoiningTermsSelect}>
                <SelectTrigger className="mt-1 h-8 text-sm">
                  <SelectValue placeholder="Select Terms & Conditions" />
                </SelectTrigger>
                <SelectContent className="bg-white max-h-60">
                  {joiningTermsList.map((term: any) => (
                    <SelectItem key={term.id} value={String(term.id)}>
                      <div className="flex flex-col">
                        <span className="text-sm font-medium">{term.title}</span>
                        <span className="text-xs text-gray-500 truncate max-w-[200px]">
                          {term.content.substring(0, 50)}...
                        </span>
                      </div>
                    </SelectItem>
                  ))}
                  <div className="border-t my-1"></div>
                  <SelectItem value="add_new" className="text-blue-600 font-medium">
                    <div className="flex items-center gap-2">
                      <Plus className="h-3 w-3" />
                      <span>+ Add New Terms & Conditions</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">Select terms & conditions for the joining letter</p>
            </div>

            {/* Preview selected terms */}
            {selectedJoiningTermsId && selectedJoiningTermsId !== "add_new" && joiningConfig.terms_and_conditions && (
              <div className="bg-gray-50 rounded-lg p-3 max-h-32 overflow-y-auto">
                <p className="text-xs text-gray-600 whitespace-pre-wrap">
                  {joiningConfig.terms_and_conditions}
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button size="sm" variant="outline" onClick={() => setShowJoiningModal(false)}>Cancel</Button>
            <Button size="sm" onClick={saveJoiningConfig} className="bg-green-600">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add New Joining Terms & Conditions Modal */}
      <Dialog open={showAddJoiningTermsModal} onOpenChange={setShowAddJoiningTermsModal}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader>
            <DialogTitle className="text-base">Add New Terms & Conditions</DialogTitle>
            <DialogDescription className="text-xs">Create new terms for joining letters</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs">Title <span className="text-red-500">*</span></Label>
              <Input
                type="text"
                value={newJoiningTermsData.title}
                onChange={(e) => setNewJoiningTermsData({ ...newJoiningTermsData, title: e.target.value })}
                className="mt-1 h-8 text-sm"
                placeholder="e.g., Standard Joining Terms, 2024 Terms"
              />
            </div>
            <div>
              <Label className="text-xs">Content <span className="text-red-500">*</span></Label>
              <textarea
                value={newJoiningTermsData.content}
                onChange={(e) => setNewJoiningTermsData({ ...newJoiningTermsData, content: e.target.value })}
                className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                rows={6}
                placeholder="Enter the terms and conditions content..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button size="sm" variant="outline" onClick={() => setShowAddJoiningTermsModal(false)}>Cancel</Button>
            <Button size="sm" onClick={handleSaveNewJoiningTerms} disabled={isSavingJoiningTerms} className="bg-blue-600">
              {isSavingJoiningTerms ? "Saving..." : "Save Terms"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Salary Slip Check Modal */}
      <Dialog open={showSalaryCheckModal} onOpenChange={setShowSalaryCheckModal}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader>
            <DialogTitle className="text-base flex items-center gap-2">
              <div className="h-2 w-2 rounded-full bg-amber-500 animate-pulse"></div>
              Salary Calculation Required
            </DialogTitle>
            <DialogDescription className="text-sm">
              Please calculate the salary in Payroll Records first
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
              <p className="text-amber-800 text-sm">
                Before configuring the salary slip, you need to calculate the salary for this employee in the <strong>Payroll Records</strong> section.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSalaryCheckModal(false)}>Cancel</Button>
            <Button onClick={() => { setShowSalaryCheckModal(false); setShowSalaryModal(true); }} className="bg-blue-600">OK</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Salary Slip Modal */}
      <Dialog open={showSalaryModal} onOpenChange={setShowSalaryModal}>
        <DialogContent className="max-w-sm bg-white">
          <DialogHeader>
            <DialogTitle className="text-base">Configure Salary Slip</DialogTitle>
            <DialogDescription className="text-xs">Select month and year</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs">Month</Label>
              <Select value={String(salaryConfig.month)} onValueChange={(value) => setSalaryConfig({ ...salaryConfig, month: parseInt(value) })}>
                <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>{monthOptions.map((m) => <SelectItem key={m.value} value={String(m.value)}>{m.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Year</Label>
              <Select value={String(salaryConfig.year)} onValueChange={(value) => setSalaryConfig({ ...salaryConfig, year: parseInt(value) })}>
                <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>{yearOptions.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button size="sm" variant="outline" onClick={() => setShowSalaryModal(false)}>Cancel</Button>
            <Button size="sm" onClick={saveSalaryConfig} className="bg-purple-600">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email Modal */}
      <Dialog open={showEmailModal} onOpenChange={setShowEmailModal}>
        <DialogContent className="max-w-sm bg-white">
          <DialogHeader>
            <DialogTitle className="text-base">Send {emailType === "offer" ? "Offer Letter" : emailType === "joining" ? "Joining Letter" : "Salary Slip"}</DialogTitle>
            <DialogDescription className="text-xs">Send as PDF attachment</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs">Recipient Email <span className="text-red-500">*</span></Label>
              <Input type="email" value={emailRecipient} onChange={(e) => setEmailRecipient(e.target.value)} placeholder="employee@company.com" className="mt-1 h-8 text-sm" />
            </div>
          </div>
          <DialogFooter>
            <Button size="sm" variant="outline" onClick={() => setShowEmailModal(false)}>Cancel</Button>
            <Button size="sm" onClick={emailType === "offer" ? handleSendOfferEmail : emailType === "joining" ? handleSendJoiningEmail : handleSendSalaryEmail} disabled={!emailRecipient || isSendingEmail || isSalaryEmailLoading} className="bg-purple-600">
              {(isSendingEmail || isSalaryEmailLoading) ? <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div> : "Send"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}